import { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle, useMemo } from 'react'

// =============================================================================
// CANVAS EDITOR — Fabric.js powered design canvas (CDN loaded)
// V2.5 — Full vector tools, shape libraries, layout, advanced bezier pen,
//         node editing, stroke-to-path, text-on-path, path offset/inset
// Fabric is loaded via <script> tag in index.html, accessed as window.fabric
// =============================================================================

const GRID_SIZE = 20
const HISTORY_LIMIT = 50
const MIN_ZOOM = 0.1
const MAX_ZOOM = 5
const SNAP_THRESHOLD = 8

// =============================================================================
// SHAPE DEFINITIONS — Basic shapes
// =============================================================================

const SHAPES = {
  rect: { label: 'Rectangle', icon: '▭' },
  circle: { label: 'Circle', icon: '○' },
  triangle: { label: 'Triangle', icon: '△' },
  star: { label: 'Star', icon: '☆' },
  heart: { label: 'Heart', icon: '♡' },
  diamond: { label: 'Diamond', icon: '◇' },
  hexagon: { label: 'Hexagon', icon: '⬡' },
  arrow: { label: 'Arrow', icon: '→' },
  line: { label: 'Line', icon: '—' },
}

// =============================================================================
// SHAPE LIBRARY — Decorative, Holiday, Craft-specific (V1.5)
// =============================================================================

const SHAPE_LIBRARY = {
  decorative: {
    label: 'Decorative',
    icon: '✨',
    shapes: [
      { id: 'flourish-1', name: 'Flourish Swirl', path: 'M0,50 C20,20 40,0 60,10 C80,20 70,50 50,50 C30,50 20,80 40,90 C60,100 80,80 100,50', scale: 0.8 },
      { id: 'flourish-2', name: 'Double Swirl', path: 'M10,50 Q30,10 50,50 Q70,90 90,50 M10,50 Q30,90 50,50 Q70,10 90,50', scale: 0.8 },
      { id: 'frame-rect', name: 'Decorative Frame', path: 'M10,0 L90,0 Q100,0 100,10 L100,90 Q100,100 90,100 L10,100 Q0,100 0,90 L0,10 Q0,0 10,0 Z M15,5 L85,5 Q95,5 95,15 L95,85 Q95,95 85,95 L15,95 Q5,95 5,85 L5,15 Q5,5 15,5 Z', scale: 1.2 },
      { id: 'frame-circle', name: 'Circle Frame', path: 'M50,0 A50,50 0 1,1 50,100 A50,50 0 1,1 50,0 Z M50,8 A42,42 0 1,0 50,92 A42,42 0 1,0 50,8 Z', scale: 1.2 },
      { id: 'frame-diamond', name: 'Diamond Frame', path: 'M50,0 L100,50 L50,100 L0,50 Z M50,10 L90,50 L50,90 L10,50 Z', scale: 1.2 },
      { id: 'monogram-circle', name: 'Monogram Circle', path: 'M50,2 A48,48 0 1,1 50,98 A48,48 0 1,1 50,2 Z M50,6 A44,44 0 1,0 50,94 A44,44 0 1,0 50,6 Z M20,50 A30,30 0 0,1 80,50 M80,50 A30,30 0 0,1 20,50', scale: 1.2 },
      { id: 'monogram-floral', name: 'Floral Frame', path: 'M50,0 C60,15 75,5 80,20 C85,35 100,40 95,55 C90,70 100,85 85,85 C70,85 65,100 50,95 C35,100 30,85 15,85 C0,85 10,70 5,55 C0,40 15,35 20,20 C25,5 40,15 50,0 Z', scale: 1.2 },
      { id: 'mandala-1', name: 'Mandala Simple', path: 'M50,10 L55,40 L85,40 L60,55 L70,85 L50,65 L30,85 L40,55 L15,40 L45,40 Z', scale: 1.3 },
      { id: 'wreath-1', name: 'Laurel Wreath', path: 'M50,95 C30,90 15,75 10,55 C5,35 15,15 35,10 C42,8 48,12 50,18 M50,95 C70,90 85,75 90,55 C95,35 85,15 65,10 C58,8 52,12 50,18 M20,45 Q25,35 35,32 M80,45 Q75,35 65,32 M15,65 Q22,58 32,58 M85,65 Q78,58 68,58', scale: 1.3 },
      { id: 'border-corner', name: 'Corner Flourish', path: 'M0,100 C0,60 10,30 30,15 C50,0 80,0 100,0 M5,100 C5,65 15,40 35,25 C50,15 70,10 90,10', scale: 0.8 },
    ]
  },
  holiday: {
    label: 'Holiday',
    icon: '🎄',
    shapes: [
      { id: 'heart-valentine', name: 'Heart', path: 'M50,30 C50,10 30,0 15,15 C0,30 0,55 50,90 C100,55 100,30 85,15 C70,0 50,10 50,30 Z', scale: 1 },
      { id: 'shamrock', name: 'Shamrock', path: 'M50,90 L50,55 M50,55 C35,55 20,45 20,35 C20,22 32,15 42,18 C48,20 50,28 50,35 M50,55 C65,55 80,45 80,35 C80,22 68,15 58,18 C52,20 50,28 50,35 M50,55 C50,40 45,25 50,15 C55,25 50,40 50,55', scale: 1.1 },
      { id: 'egg', name: 'Easter Egg', path: 'M50,5 C72,5 90,30 90,55 C90,78 72,95 50,95 C28,95 10,78 10,55 C10,30 28,5 50,5 Z', scale: 1 },
      { id: 'bunny', name: 'Bunny', path: 'M35,35 C35,15 38,0 42,0 C46,0 48,15 48,30 M52,30 C52,15 54,0 58,0 C62,0 65,15 65,35 M50,90 C30,90 15,75 15,60 C15,42 30,30 50,30 C70,30 85,42 85,60 C85,75 70,90 50,90 Z', scale: 1 },
      { id: 'star-4th', name: '5-Point Star', path: 'M50,0 L61,35 L98,35 L68,57 L79,91 L50,70 L21,91 L32,57 L2,35 L39,35 Z', scale: 1.2 },
      { id: 'firework', name: 'Firework Burst', path: 'M50,50 L50,10 M50,50 L85,20 M50,50 L90,50 M50,50 L85,80 M50,50 L50,90 M50,50 L15,80 M50,50 L10,50 M50,50 L15,20', scale: 1.2 },
      { id: 'pumpkin', name: 'Pumpkin', path: 'M50,15 C50,10 48,5 50,3 C52,5 50,10 50,15 M50,20 C25,20 5,40 5,60 C5,85 25,95 50,95 C75,95 95,85 95,60 C95,40 75,20 50,20 Z M30,25 C20,30 12,45 12,60 M70,25 C80,30 88,45 88,60', scale: 1.1 },
      { id: 'ghost', name: 'Ghost', path: 'M25,90 L25,40 C25,20 37,5 50,5 C63,5 75,20 75,40 L75,90 L65,80 L55,90 L45,80 L35,90 L25,80 Z M38,35 A4,4 0 1,1 38,43 A4,4 0 1,1 38,35 M58,35 A4,4 0 1,1 58,43 A4,4 0 1,1 58,35', scale: 1.1 },
      { id: 'xmas-tree', name: 'Christmas Tree', path: 'M50,5 L30,35 L38,35 L20,60 L30,60 L10,90 L90,90 L70,60 L80,60 L62,35 L70,35 Z M46,90 L46,100 L54,100 L54,90', scale: 1.2 },
      { id: 'snowflake', name: 'Snowflake', path: 'M50,10 L50,90 M18,30 L82,70 M82,30 L18,70 M50,25 L40,18 M50,25 L60,18 M50,75 L40,82 M50,75 L60,82 M30,38 L22,30 M30,38 L22,46 M70,62 L78,54 M70,62 L78,70 M70,38 L78,30 M70,38 L78,46 M30,62 L22,54 M30,62 L22,70', scale: 1.3 },
    ]
  },
  craft: {
    label: 'Craft',
    icon: '🎨',
    shapes: [
      { id: 'arrow-banner', name: 'Banner Arrow', path: 'M0,20 L80,20 L100,50 L80,80 L0,80 L20,50 Z', scale: 1.2 },
      { id: 'ribbon-banner', name: 'Ribbon Banner', path: 'M5,25 L25,25 L25,15 L75,15 L75,25 L95,25 L85,40 L95,55 L75,55 L75,65 L25,65 L25,55 L5,55 L15,40 Z', scale: 1.2 },
      { id: 'speech-bubble', name: 'Speech Bubble', path: 'M10,10 L90,10 Q95,10 95,15 L95,60 Q95,65 90,65 L40,65 L25,80 L30,65 L10,65 Q5,65 5,60 L5,15 Q5,10 10,10 Z', scale: 1.1 },
      { id: 'thought-bubble', name: 'Thought Bubble', path: 'M50,10 C75,10 90,25 90,42 C90,55 80,65 65,68 C65,68 55,75 45,80 M40,85 A5,5 0 1,1 40,86 M33,92 A3,3 0 1,1 33,93 M50,10 C25,10 10,25 10,42 C10,55 20,65 35,68', scale: 1.1 },
      { id: 'label-rect', name: 'Rectangle Label', path: 'M15,10 L85,10 Q95,10 95,20 L95,70 Q95,80 85,80 L15,80 Q5,80 5,70 L5,20 Q5,10 15,10 Z', scale: 1 },
      { id: 'label-oval', name: 'Oval Label', path: 'M50,10 C78,10 95,25 95,45 C95,65 78,80 50,80 C22,80 5,65 5,45 C5,25 22,10 50,10 Z', scale: 1.1 },
      { id: 'tag', name: 'Gift Tag', path: 'M50,5 A8,8 0 1,1 50,6 M30,20 L15,20 Q5,20 5,30 L5,90 Q5,95 10,95 L90,95 Q95,95 95,90 L95,30 Q95,20 85,20 L70,20 A20,20 0 0,1 30,20 Z', scale: 1 },
      { id: 'bow', name: 'Bow', path: 'M50,45 C35,35 10,20 15,40 C20,55 40,50 50,45 M50,45 C65,35 90,20 85,40 C80,55 60,50 50,45 M45,45 L40,80 M55,45 L60,80 M42,50 C42,50 50,55 58,50', scale: 1.1 },
      { id: 'scroll', name: 'Scroll', path: 'M15,15 C15,5 25,5 25,15 L25,80 C25,90 15,90 15,80 L15,15 M25,15 L85,15 L85,80 C85,90 75,90 75,80 L75,15 M85,80 C85,90 95,90 95,80 L95,20', scale: 1 },
    ]
  }
}

// =============================================================================
// SHAPE GEOMETRY HELPERS
// =============================================================================

const createStarPoints = (cx, cy, outerR, innerR, points = 5) => {
  const result = []
  const step = Math.PI / points
  for (let i = 0; i < 2 * points; i++) {
    const r = i % 2 === 0 ? outerR : innerR
    const angle = i * step - Math.PI / 2
    result.push({ x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) })
  }
  return result
}

const createHeartPath = (size) => {
  const s = size / 2
  return `M ${s},${s * 0.3} C ${s},${-s * 0.3} ${s * 0.3},${-s * 0.6} 0,${-s * 0.3} C ${-s * 0.3},${-s * 0.6} ${-s},${-s * 0.3} ${-s},${s * 0.3} C ${-s},${s * 0.6} ${-s * 0.3},${s * 0.8} 0,${s} C ${s * 0.3},${s * 0.8} ${s},${s * 0.6} ${s},${s * 0.3} Z`
}

const createHexagonPoints = (cx, cy, r) => {
  const points = []
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 3) * i - Math.PI / 6
    points.push({ x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) })
  }
  return points
}

const createArrowPoints = () => [
  { x: 0, y: 30 }, { x: 60, y: 30 }, { x: 60, y: 0 },
  { x: 100, y: 50 }, { x: 60, y: 100 }, { x: 60, y: 70 }, { x: 0, y: 70 }
]

const getFabric = () => window.fabric

// =============================================================================
// ADVANCED VECTOR UTILITIES (V1.3)
// =============================================================================

// Parse SVG path into array of segments [{type, points}]
const parsePath = (pathStr) => {
  if (!pathStr) return []
  const segments = []
  const re = /([MLHVCSQTAZ])([^MLHVCSQTAZ]*)/gi
  let match
  while ((match = re.exec(pathStr)) !== null) {
    const cmd = match[1]
    const nums = match[2].trim().split(/[\s,]+/).filter(Boolean).map(Number)
    const points = []
    for (let i = 0; i < nums.length; i += 2) {
      if (i + 1 < nums.length) points.push({ x: nums[i], y: nums[i + 1] })
      else points.push({ x: nums[i], y: 0 })
    }
    segments.push({ cmd: cmd.toUpperCase(), points, raw: nums })
  }
  return segments
}

// Build SVG path string from segments
const buildPath = (segments) => {
  return segments.map(seg => {
    if (seg.cmd === 'Z') return 'Z'
    return `${seg.cmd} ${seg.raw.join(' ')}`
  }).join(' ')
}

// Get all anchor points from a Fabric.js path object
const getPathAnchors = (pathObj) => {
  if (!pathObj || !pathObj.path) return []
  const anchors = []
  const path = pathObj.path
  const ox = pathObj.pathOffset?.x || 0
  const oy = pathObj.pathOffset?.y || 0
  const sx = pathObj.scaleX || 1
  const sy = pathObj.scaleY || 1
  const left = pathObj.left || 0
  const top = pathObj.top || 0

  path.forEach((seg, idx) => {
    const cmd = seg[0]
    if (cmd === 'M' || cmd === 'L') {
      anchors.push({
        idx, type: 'anchor', cmd,
        x: (seg[1] - ox) * sx + left,
        y: (seg[2] - oy) * sy + top,
        pathIdx: [1, 2]
      })
    } else if (cmd === 'Q') {
      anchors.push({
        idx, type: 'control', cmd,
        x: (seg[1] - ox) * sx + left,
        y: (seg[2] - oy) * sy + top,
        pathIdx: [1, 2]
      })
      anchors.push({
        idx, type: 'anchor', cmd,
        x: (seg[3] - ox) * sx + left,
        y: (seg[4] - oy) * sy + top,
        pathIdx: [3, 4]
      })
    } else if (cmd === 'C') {
      anchors.push({
        idx, type: 'control', cmd, handleFor: 'cp1',
        x: (seg[1] - ox) * sx + left,
        y: (seg[2] - oy) * sy + top,
        pathIdx: [1, 2]
      })
      anchors.push({
        idx, type: 'control', cmd, handleFor: 'cp2',
        x: (seg[3] - ox) * sx + left,
        y: (seg[4] - oy) * sy + top,
        pathIdx: [3, 4]
      })
      anchors.push({
        idx, type: 'anchor', cmd,
        x: (seg[5] - ox) * sx + left,
        y: (seg[6] - oy) * sy + top,
        pathIdx: [5, 6]
      })
    }
  })
  return anchors
}

// Update a path's anchor position (mutates path data)
const updatePathAnchor = (pathObj, segIdx, pathIdxArr, dx, dy) => {
  if (!pathObj?.path?.[segIdx]) return
  const sx = pathObj.scaleX || 1
  const sy = pathObj.scaleY || 1
  pathObj.path[segIdx][pathIdxArr[0]] += dx / sx
  pathObj.path[segIdx][pathIdxArr[1]] += dy / sy
  // Force Fabric to recalculate
  pathObj.set({ dirty: true })
  pathObj.canvas?.renderAll()
}

// Stroke-to-path: expand stroke into filled shape
const strokeToPath = (obj, canvas) => {
  if (!obj || !canvas) return null
  const f = getFabric()
  const sw = obj.strokeWidth || 2
  const stroke = obj.stroke || '#000000'

  // Clone and modify
  obj.clone((cloned) => {
    cloned.set({
      fill: stroke,
      stroke: 'transparent',
      strokeWidth: 0,
      // Expand bounds by stroke width
      scaleX: (cloned.scaleX || 1) * (1 + sw / (cloned.width || 100)),
      scaleY: (cloned.scaleY || 1) * (1 + sw / (cloned.height || 100)),
    })
    cloned._layerId = `layer_${Date.now()}_stk`
    cloned._layerName = `Stroke→Fill ${obj._layerName || ''}`
    canvas.add(cloned)
    canvas.renderAll()
  })
}

// Path offset: scale from center by an absolute amount
const pathOffset = (obj, canvas, amount) => {
  if (!obj || !canvas) return
  const f = getFabric()
  obj.clone((cloned) => {
    const cx = cloned.left + (cloned.width * (cloned.scaleX || 1)) / 2
    const cy = cloned.top + (cloned.height * (cloned.scaleY || 1)) / 2
    const w = cloned.width * (cloned.scaleX || 1)
    const h = cloned.height * (cloned.scaleY || 1)
    const sx = (w + amount * 2) / w
    const sy = (h + amount * 2) / h
    cloned.set({
      scaleX: (cloned.scaleX || 1) * sx,
      scaleY: (cloned.scaleY || 1) * sy,
      left: cx - (w * sx) / 2,
      top: cy - (h * sy) / 2,
      opacity: 0.6,
    })
    cloned._layerId = `layer_${Date.now()}_off`
    cloned._layerName = `${amount > 0 ? 'Offset' : 'Inset'} ${Math.abs(amount)}px`
    canvas.add(cloned)
    canvas.sendToBack(cloned)
    canvas.renderAll()
  })
}

// Text on path: position characters along a path
const textOnPath = (text, pathObj, canvas, options = {}) => {
  const f = getFabric()
  if (!f || !canvas || !pathObj?.path) return

  const { fontSize = 24, fontFamily = 'Arial', fill = '#0F172A', spacing = 0 } = options
  const path = pathObj.path
  const ox = pathObj.pathOffset?.x || 0
  const oy = pathObj.pathOffset?.y || 0
  const sx = pathObj.scaleX || 1
  const sy = pathObj.scaleY || 1
  const pl = pathObj.left || 0
  const pt = pathObj.top || 0

  // Collect points along path segments
  const pathPoints = []
  let prevX = 0, prevY = 0
  path.forEach(seg => {
    const cmd = seg[0]
    if (cmd === 'M') { prevX = seg[1]; prevY = seg[2] }
    else if (cmd === 'L') {
      for (let t = 0; t <= 1; t += 0.05) {
        pathPoints.push({
          x: (prevX + (seg[1] - prevX) * t - ox) * sx + pl,
          y: (prevY + (seg[2] - prevY) * t - oy) * sy + pt,
          angle: Math.atan2(seg[2] - prevY, seg[1] - prevX) * 180 / Math.PI
        })
      }
      prevX = seg[1]; prevY = seg[2]
    } else if (cmd === 'Q') {
      for (let t = 0; t <= 1; t += 0.05) {
        const t1 = 1 - t
        const x = t1 * t1 * prevX + 2 * t1 * t * seg[1] + t * t * seg[3]
        const y = t1 * t1 * prevY + 2 * t1 * t * seg[2] + t * t * seg[4]
        const dx = 2 * t1 * (seg[1] - prevX) + 2 * t * (seg[3] - seg[1])
        const dy = 2 * t1 * (seg[2] - prevY) + 2 * t * (seg[4] - seg[2])
        pathPoints.push({
          x: (x - ox) * sx + pl,
          y: (y - oy) * sy + pt,
          angle: Math.atan2(dy, dx) * 180 / Math.PI
        })
      }
      prevX = seg[3]; prevY = seg[4]
    } else if (cmd === 'C') {
      for (let t = 0; t <= 1; t += 0.03) {
        const t1 = 1 - t
        const x = t1*t1*t1*prevX + 3*t1*t1*t*seg[1] + 3*t1*t*t*seg[3] + t*t*t*seg[5]
        const y = t1*t1*t1*prevY + 3*t1*t1*t*seg[2] + 3*t1*t*t*seg[4] + t*t*t*seg[6]
        const dx = 3*t1*t1*(seg[1]-prevX) + 6*t1*t*(seg[3]-seg[1]) + 3*t*t*(seg[5]-seg[3])
        const dy = 3*t1*t1*(seg[2]-prevY) + 6*t1*t*(seg[4]-seg[2]) + 3*t*t*(seg[6]-seg[4])
        pathPoints.push({
          x: (x - ox) * sx + pl,
          y: (y - oy) * sy + pt,
          angle: Math.atan2(dy, dx) * 180 / Math.PI
        })
      }
      prevX = seg[5]; prevY = seg[6]
    }
  })

  if (pathPoints.length === 0) return

  // Place each character along path
  const charWidth = fontSize * 0.6 + spacing
  const group = []
  let pointIdx = 0
  const totalLen = pathPoints.length
  const startOffset = Math.max(0, Math.floor((totalLen - text.length * (charWidth / (totalLen > 0 ? 1 : charWidth))) / 2))

  for (let i = 0; i < text.length; i++) {
    pointIdx = Math.min(Math.floor(startOffset + i * (totalLen / text.length)), totalLen - 1)
    const pt = pathPoints[pointIdx]
    if (!pt) continue
    const charObj = new f.Text(text[i], {
      left: pt.x,
      top: pt.y,
      fontSize,
      fontFamily,
      fill,
      originX: 'center',
      originY: 'center',
      angle: pt.angle,
      selectable: false,
      evented: false,
    })
    group.push(charObj)
  }

  if (group.length > 0) {
    const textGroup = new f.Group(group, { originX: 'center', originY: 'center' })
    textGroup._layerId = `layer_${Date.now()}_top`
    textGroup._layerName = `Text on Path: "${text.slice(0, 15)}${text.length > 15 ? '...' : ''}"`
    canvas.add(textGroup)
    canvas.setActiveObject(textGroup)
    canvas.renderAll()
  }
}

// =============================================================================
// LAYER EFFECTS PANEL
// =============================================================================
const LayerEffectsPanel = ({ object, canvas, onUpdate }) => {
  const [opacity, setOpacity] = useState(100)
  const [shadowEnabled, setShadowEnabled] = useState(false)
  const [shadowColor, setShadowColor] = useState('#000000')
  const [shadowBlur, setShadowBlur] = useState(10)
  const [shadowOffsetX, setShadowOffsetX] = useState(5)
  const [shadowOffsetY, setShadowOffsetY] = useState(5)

  useEffect(() => {
    if (!object) return
    setOpacity(Math.round((object.opacity || 1) * 100))
    const s = object.shadow
    if (s && typeof s === 'object') {
      setShadowEnabled(true)
      setShadowColor(s.color || '#000000')
      setShadowBlur(s.blur || 10)
      setShadowOffsetX(s.offsetX || 5)
      setShadowOffsetY(s.offsetY || 5)
    } else {
      setShadowEnabled(false)
    }
  }, [object])

  const apply = (updates) => {
    if (!object || !canvas) return
    Object.assign(object, updates)
    canvas.renderAll()
    onUpdate?.()
  }

  if (!object) return null

  return (
    <div className="p-2 space-y-2 border-t border-slate-200 dark:border-slate-700">
      <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Effects</span>
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-slate-500 w-12">Opacity</span>
        <input type="range" min="0" max="100" value={opacity} className="flex-1 h-1"
          onChange={(e) => { const v = parseInt(e.target.value); setOpacity(v); apply({ opacity: v / 100 }) }} />
        <span className="text-[10px] text-slate-500 w-8 text-right">{opacity}%</span>
      </div>
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={shadowEnabled} className="rounded border-slate-300"
          onChange={(e) => {
            setShadowEnabled(e.target.checked)
            if (e.target.checked) {
              const f = getFabric()
              apply({ shadow: new f.Shadow({ color: shadowColor, blur: shadowBlur, offsetX: shadowOffsetX, offsetY: shadowOffsetY }) })
            } else { apply({ shadow: null }) }
          }} />
        <span className="text-[10px] text-slate-600 dark:text-slate-300">Drop Shadow</span>
      </label>
      {shadowEnabled && (
        <div className="pl-4 space-y-1.5">
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-slate-500 w-10">Color</span>
            <input type="color" value={shadowColor} className="w-6 h-6 rounded cursor-pointer border-0"
              onChange={(e) => { setShadowColor(e.target.value); const f = getFabric(); apply({ shadow: new f.Shadow({ color: e.target.value, blur: shadowBlur, offsetX: shadowOffsetX, offsetY: shadowOffsetY }) }) }} />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-slate-500 w-10">Blur</span>
            <input type="range" min="0" max="40" value={shadowBlur} className="flex-1 h-1"
              onChange={(e) => { const v = parseInt(e.target.value); setShadowBlur(v); const f = getFabric(); apply({ shadow: new f.Shadow({ color: shadowColor, blur: v, offsetX: shadowOffsetX, offsetY: shadowOffsetY }) }) }} />
            <span className="text-[10px] text-slate-500 w-6">{shadowBlur}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-slate-500 w-10">X / Y</span>
            <input type="range" min="-20" max="20" value={shadowOffsetX} className="flex-1 h-1"
              onChange={(e) => { const v = parseInt(e.target.value); setShadowOffsetX(v); const f = getFabric(); apply({ shadow: new f.Shadow({ color: shadowColor, blur: shadowBlur, offsetX: v, offsetY: shadowOffsetY }) }) }} />
            <input type="range" min="-20" max="20" value={shadowOffsetY} className="flex-1 h-1"
              onChange={(e) => { const v = parseInt(e.target.value); setShadowOffsetY(v); const f = getFabric(); apply({ shadow: new f.Shadow({ color: shadowColor, blur: shadowBlur, offsetX: shadowOffsetX, offsetY: v }) }) }} />
          </div>
        </div>
      )}
    </div>
  )
}

// =============================================================================
// LAYER PANEL (enhanced with groups)
// =============================================================================
const LayerPanel = ({ layers, activeId, activeObject, canvas, onSelect, onToggleVisible, onToggleLock, onReorder, onDelete, onRename, onGroup, onUngroup, onEffectsUpdate }) => {
  const [editingId, setEditingId] = useState(null)
  const [editName, setEditName] = useState('')
  const [showEffects, setShowEffects] = useState(false)
  const [lpFontSearch, setLpFontSearch] = useState('')
  const [lpFontResults, setLpFontResults] = useState([])
  const [lpShowFonts, setLpShowFonts] = useState(false)
  const [lpFontCategory, setLpFontCategory] = useState(null)
  const lpFontCache = useRef(null)
  const lpFontTimer = useRef(null)

  const isTextObj = activeObject && (activeObject.type === 'i-text' || activeObject.type === 'text' || activeObject.type === 'textbox')

  const loadFonts = async (query = '', category = null) => {
    try {
      if (!lpFontCache.current) {
        const res = await fetch('https://www.googleapis.com/webfonts/v1/webfonts?key=AIzaSyDZKSU6e1rl5ccUVh0aLv_a8XEMSmFROvY&sort=popularity')
        const data = await res.json()
        lpFontCache.current = (data.items || []).map(f => ({ family: f.family, category: f.category }))
      }
      let matches = lpFontCache.current
      if (category) matches = matches.filter(f => f.category === category)
      if (query) matches = matches.filter(f => f.family.toLowerCase().includes(query.toLowerCase()))
      const results = matches.slice(0, 40)
      setLpFontResults(results.map(f => f.family))
      // Preload font CSS
      results.forEach(f => {
        const linkId = `gfont-${f.family.replace(/\s/g, '-')}`
        if (!document.getElementById(linkId)) {
          const link = document.createElement('link')
          link.id = linkId; link.rel = 'stylesheet'
          link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(f.family)}&display=swap`
          document.head.appendChild(link)
        }
      })
    } catch (e) { setLpFontResults([]) }
  }

  const applyFont = (fontName) => {
    if (!activeObject || !canvas) return
    const linkId = `gfont-${fontName.replace(/\s/g, '-')}`
    if (!document.getElementById(linkId)) {
      const link = document.createElement('link')
      link.id = linkId; link.rel = 'stylesheet'
      link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}&display=swap`
      document.head.appendChild(link)
    }
    setTimeout(() => { activeObject.set('fontFamily', fontName); canvas.renderAll(); onEffectsUpdate() }, 300)
    setLpFontSearch(fontName)
    setLpShowFonts(false)
  }

  const startRename = (layer) => { setEditingId(layer.id); setEditName(layer.name) }
  const commitRename = () => {
    if (editingId && editName.trim()) onRename(editingId, editName.trim())
    setEditingId(null)
  }

  return (
    <div className="w-56 bg-white dark:bg-slate-800 border-l border-slate-200 dark:border-slate-700 flex flex-col overflow-hidden">
      <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Layers</span>
        <div className="flex items-center gap-1">
          <button onClick={onGroup} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-600" title="Group selected">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5z" /></svg>
          </button>
          <button onClick={onUngroup} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-600" title="Ungroup">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h8m-8 4h8m-8 4h8" /></svg>
          </button>
          <button onClick={() => setShowEffects(!showEffects)} className={`p-1 rounded ${showEffects ? 'bg-zenna-violet/10 text-zenna-violet' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700'}`} title="Effects">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" /></svg>
          </button>
          <span className="text-[10px] text-slate-400 dark:text-slate-500 ml-1">{layers.length}</span>
        </div>
      </div>

      {showEffects && activeObject && (
        <LayerEffectsPanel object={activeObject} canvas={canvas} onUpdate={onEffectsUpdate} />
      )}

      {/* TEXT PROPERTIES — shows in sidebar when text is selected */}
      {isTextObj && (
        <div className="border-b border-slate-200 dark:border-slate-700 p-3 space-y-2">
          <div className="text-[10px] font-semibold text-zenna-violet uppercase tracking-wider">Text Properties</div>

          {/* Font search */}
          <div className="relative">
            <input
              type="text"
              value={lpFontSearch || activeObject.fontFamily || 'Arial'}
              onChange={(e) => {
                setLpFontSearch(e.target.value)
                setLpShowFonts(true)
                if (lpFontTimer.current) clearTimeout(lpFontTimer.current)
                lpFontTimer.current = setTimeout(() => loadFonts(e.target.value, lpFontCategory), 200)
              }}
              onFocus={() => { setLpShowFonts(true); loadFonts(lpFontSearch || '', lpFontCategory) }}
              placeholder="Search fonts..."
              className="w-full text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-white"
            />
            {lpShowFonts && (
              <div className="absolute left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl max-h-[300px] flex flex-col" style={{ zIndex: 9999 }}>
                {/* Category tags */}
                <div className="flex flex-wrap gap-1 p-2 border-b border-slate-200 dark:border-slate-700">
                  {[
                    { key: null, label: 'All' },
                    { key: 'serif', label: 'Serif' },
                    { key: 'sans-serif', label: 'Sans' },
                    { key: 'display', label: 'Display' },
                    { key: 'handwriting', label: 'Script' },
                    { key: 'monospace', label: 'Mono' },
                  ].map(cat => (
                    <button key={cat.key || 'all'}
                      onClick={() => { setLpFontCategory(cat.key); loadFonts(lpFontSearch || '', cat.key) }}
                      className={`px-2 py-0.5 text-[10px] font-medium rounded-full transition ${lpFontCategory === cat.key ? 'bg-zenna-violet text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-500 hover:bg-slate-200'}`}
                    >{cat.label}</button>
                  ))}
                </div>
                {/* Font list */}
                <div className="flex-1 overflow-y-auto">
                  {lpFontResults.map(f => (
                    <button key={f} onClick={() => applyFont(f)}
                      className="w-full text-left px-3 py-2 hover:bg-zenna-violet/10 transition border-b border-slate-100 dark:border-slate-700/50"
                    >
                      <span className="text-sm text-slate-800 dark:text-white" style={{ fontFamily: `'${f}', sans-serif` }}>{f}</span>
                    </button>
                  ))}
                  {lpFontResults.length === 0 && <div className="text-center text-xs text-slate-400 py-4">Loading fonts...</div>}
                </div>
              </div>
            )}
          </div>

          {/* Size + Bold/Italic row */}
          <div className="flex items-center gap-2">
            <input type="number" value={Math.round(activeObject.fontSize || 32)}
              onChange={(e) => { activeObject.set('fontSize', parseInt(e.target.value) || 32); canvas.renderAll(); onEffectsUpdate() }}
              className="w-14 text-xs px-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-center"
            />
            <button onClick={() => { activeObject.set('fontWeight', activeObject.fontWeight === 'bold' ? 'normal' : 'bold'); canvas.renderAll(); onEffectsUpdate() }}
              className={`px-2 py-1.5 rounded-lg text-xs font-bold transition ${activeObject.fontWeight === 'bold' ? 'bg-zenna-violet text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-600'}`}>B</button>
            <button onClick={() => { activeObject.set('fontStyle', activeObject.fontStyle === 'italic' ? 'normal' : 'italic'); canvas.renderAll(); onEffectsUpdate() }}
              className={`px-2 py-1.5 rounded-lg text-xs italic transition ${activeObject.fontStyle === 'italic' ? 'bg-zenna-violet text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-600'}`}>I</button>
            <input type="color" value={activeObject.fill || '#000000'}
              onChange={(e) => { activeObject.set('fill', e.target.value); canvas.renderAll(); onEffectsUpdate() }}
              className="w-8 h-8 rounded-lg cursor-pointer border-0 p-0"
            />
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        {layers.length === 0 ? (
          <div className="p-4 text-center text-xs text-slate-400 dark:text-slate-500">No layers yet. Add shapes or images.</div>
        ) : (
          <div className="py-1">
            {[...layers].reverse().map((layer) => (
              <div key={layer.id} onClick={() => onSelect(layer.id)}
                className={`group flex items-center gap-2 px-2 py-1.5 mx-1 rounded-lg cursor-pointer transition-colors ${
                  activeId === layer.id ? 'bg-zenna-violet/10 dark:bg-zenna-violet/20 border border-zenna-violet/30' : 'hover:bg-slate-50 dark:hover:bg-slate-700/50 border border-transparent'
                }`}>
                <div className={`w-7 h-7 rounded bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-xs flex-shrink-0 ${!layer.visible ? 'opacity-30' : ''}`}>
                  {layer.type === 'group' ? '📁' : layer.type === 'image' ? '🖼' : layer.type === 'i-text' || layer.type === 'textbox' ? 'T' : layer.type === 'path' ? '✏️' : layer.type === 'rect' ? '▭' : layer.type === 'circle' ? '○' : layer.type === 'triangle' ? '△' : layer.type === 'polygon' ? '⬡' : layer.type === 'line' ? '—' : '◆'}
                </div>
                <div className="flex-1 min-w-0">
                  {editingId === layer.id ? (
                    <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} onBlur={commitRename} onKeyDown={(e) => e.key === 'Enter' && commitRename()}
                      className="w-full text-xs bg-white dark:bg-slate-600 border border-zenna-violet rounded px-1.5 py-0.5 focus:outline-none" autoFocus onClick={(e) => e.stopPropagation()} />
                  ) : (
                    <span className={`text-xs truncate block ${!layer.visible ? 'text-slate-300 dark:text-slate-600' : 'text-slate-700 dark:text-slate-200'}`}
                      onDoubleClick={(e) => { e.stopPropagation(); startRename(layer) }}>{layer.name}</span>
                  )}
                </div>
                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={(e) => { e.stopPropagation(); onToggleVisible(layer.id) }}
                    className={`p-0.5 rounded hover:bg-slate-200 dark:hover:bg-slate-600 ${layer.visible ? 'text-slate-500' : 'text-slate-300'}`} title={layer.visible ? 'Hide' : 'Show'}>
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      {layer.visible ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M3 3l18 18" />}
                    </svg>
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); onToggleLock(layer.id) }}
                    className={`p-0.5 rounded hover:bg-slate-200 dark:hover:bg-slate-600 ${layer.locked ? 'text-amber-500' : 'text-slate-500'}`} title={layer.locked ? 'Unlock' : 'Lock'}>
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      {layer.locked ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />}
                    </svg>
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); onReorder(layer.id, 'up') }} className="p-0.5 rounded hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-400" title="Move up">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); onReorder(layer.id, 'down') }} className="p-0.5 rounded hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-400" title="Move down">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); onDelete(layer.id) }} className="p-0.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-slate-400 hover:text-red-500" title="Delete">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}


// =============================================================================
// SHAPE LIBRARY PANEL (V1.5)
// =============================================================================
const ShapeLibraryPanel = ({ isOpen, onClose, onAddShape }) => {
  const [activeCategory, setActiveCategory] = useState('decorative')
  const [search, setSearch] = useState('')

  if (!isOpen) return null

  const categories = Object.entries(SHAPE_LIBRARY)
  const filtered = useMemo(() => {
    const cat = SHAPE_LIBRARY[activeCategory]
    if (!cat) return []
    if (!search.trim()) return cat.shapes
    const q = search.toLowerCase()
    return cat.shapes.filter(s => s.name.toLowerCase().includes(q))
  }, [activeCategory, search])

  return (
    <div className="absolute top-full left-0 mt-1 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 z-50 w-72 max-h-80 overflow-hidden flex flex-col">
      <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">Shape Library</span>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
      </div>
      <div className="px-3 py-2">
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search shapes..."
          className="w-full text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 focus:outline-none focus:ring-1 focus:ring-zenna-violet/50" />
      </div>
      <div className="flex border-b border-slate-200 dark:border-slate-700 px-1">
        {categories.map(([key, cat]) => (
          <button key={key} onClick={() => { setActiveCategory(key); setSearch('') }}
            className={`flex-1 px-2 py-1.5 text-[10px] font-medium transition-colors ${activeCategory === key ? 'text-zenna-violet border-b-2 border-zenna-violet' : 'text-slate-500 hover:text-slate-700'}`}>
            <span className="mr-0.5">{cat.icon}</span> {cat.label}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto p-2 grid grid-cols-3 gap-1.5">
        {filtered.map((shape) => (
          <button key={shape.id} onClick={() => { onAddShape(shape); onClose() }}
            className="flex flex-col items-center p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors border border-transparent hover:border-zenna-violet/30">
            <svg viewBox="0 0 100 100" className="w-10 h-10 text-slate-600 dark:text-slate-300">
              <path d={shape.path} fill="none" stroke="currentColor" strokeWidth="2" />
            </svg>
            <span className="text-[9px] text-slate-500 dark:text-slate-400 mt-1 text-center leading-tight">{shape.name}</span>
          </button>
        ))}
        {filtered.length === 0 && <div className="col-span-3 text-center text-xs text-slate-400 py-4">No shapes found</div>}
      </div>
    </div>
  )
}


// =============================================================================
// ALIGNMENT/LAYOUT TOOLBAR (V2.0)
// =============================================================================
const AlignmentToolbar = ({ canvas, pushHistory }) => {
  const getSelection = () => {
    if (!canvas) return []
    const active = canvas.getActiveObject()
    if (!active) return []
    if (active.type === 'activeSelection') return active.getObjects()
    return [active]
  }

  const align = (direction) => {
    const objects = getSelection()
    if (objects.length === 0) return
    const active = canvas.getActiveObject()
    if (!active) return

    if (objects.length === 1) {
      // Align to canvas
      const obj = objects[0]
      const bound = obj.getBoundingRect()
      if (direction === 'left') obj.set({ left: obj.left - bound.left })
      else if (direction === 'centerH') obj.set({ left: obj.left + (canvas.width / 2 - bound.left - bound.width / 2) })
      else if (direction === 'right') obj.set({ left: obj.left + (canvas.width - bound.left - bound.width) })
      else if (direction === 'top') obj.set({ top: obj.top - bound.top })
      else if (direction === 'centerV') obj.set({ top: obj.top + (canvas.height / 2 - bound.top - bound.height / 2) })
      else if (direction === 'bottom') obj.set({ top: obj.top + (canvas.height - bound.top - bound.height) })
      obj.setCoords()
    } else {
      // Align relative to selection bounds
      const bounds = objects.map(o => o.getBoundingRect())
      const groupBound = active.getBoundingRect()
      objects.forEach((obj, i) => {
        const b = bounds[i]
        if (direction === 'left') obj.set({ left: obj.left + (groupBound.left - b.left) })
        else if (direction === 'centerH') obj.set({ left: obj.left + (groupBound.left + groupBound.width / 2 - b.left - b.width / 2) })
        else if (direction === 'right') obj.set({ left: obj.left + (groupBound.left + groupBound.width - b.left - b.width) })
        else if (direction === 'top') obj.set({ top: obj.top + (groupBound.top - b.top) })
        else if (direction === 'centerV') obj.set({ top: obj.top + (groupBound.top + groupBound.height / 2 - b.top - b.height / 2) })
        else if (direction === 'bottom') obj.set({ top: obj.top + (groupBound.top + groupBound.height - b.top - b.height) })
        obj.setCoords()
      })
    }
    canvas.renderAll()
    pushHistory?.()
  }

  const distribute = (axis) => {
    const objects = getSelection()
    if (objects.length < 3) return
    const bounds = objects.map((o, i) => ({ obj: o, bound: o.getBoundingRect(), idx: i }))
    if (axis === 'horizontal') {
      bounds.sort((a, b) => a.bound.left - b.bound.left)
      const totalWidth = bounds.reduce((s, b) => s + b.bound.width, 0)
      const totalSpace = bounds[bounds.length - 1].bound.left + bounds[bounds.length - 1].bound.width - bounds[0].bound.left
      const gap = (totalSpace - totalWidth) / (bounds.length - 1)
      let x = bounds[0].bound.left
      bounds.forEach((b) => {
        b.obj.set({ left: b.obj.left + (x - b.bound.left) })
        b.obj.setCoords()
        x += b.bound.width + gap
      })
    } else {
      bounds.sort((a, b) => a.bound.top - b.bound.top)
      const totalHeight = bounds.reduce((s, b) => s + b.bound.height, 0)
      const totalSpace = bounds[bounds.length - 1].bound.top + bounds[bounds.length - 1].bound.height - bounds[0].bound.top
      const gap = (totalSpace - totalHeight) / (bounds.length - 1)
      let y = bounds[0].bound.top
      bounds.forEach((b) => {
        b.obj.set({ top: b.obj.top + (y - b.bound.top) })
        b.obj.setCoords()
        y += b.bound.height + gap
      })
    }
    canvas.renderAll()
    pushHistory?.()
  }

  const flip = (axis) => {
    const active = canvas?.getActiveObject()
    if (!active) return
    if (axis === 'h') active.set({ flipX: !active.flipX })
    else active.set({ flipY: !active.flipY })
    canvas.renderAll()
    pushHistory?.()
  }

  const btnCls = "p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"

  return (
    <div className="flex items-center gap-0.5">
      <button onClick={() => align('left')} className={btnCls} title="Align Left">
        <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="currentColor"><rect x="0" y="0" width="2" height="16" /><rect x="4" y="2" width="8" height="4" /><rect x="4" y="10" width="12" height="4" /></svg>
      </button>
      <button onClick={() => align('centerH')} className={btnCls} title="Center Horizontal">
        <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="currentColor"><rect x="7" y="0" width="2" height="16" /><rect x="2" y="2" width="12" height="4" /><rect x="4" y="10" width="8" height="4" /></svg>
      </button>
      <button onClick={() => align('right')} className={btnCls} title="Align Right">
        <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="currentColor"><rect x="14" y="0" width="2" height="16" /><rect x="4" y="2" width="8" height="4" /><rect x="0" y="10" width="12" height="4" /></svg>
      </button>
      <button onClick={() => align('top')} className={btnCls} title="Align Top">
        <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="currentColor"><rect x="0" y="0" width="16" height="2" /><rect x="2" y="4" width="4" height="8" /><rect x="10" y="4" width="4" height="12" /></svg>
      </button>
      <button onClick={() => align('centerV')} className={btnCls} title="Center Vertical">
        <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="currentColor"><rect x="0" y="7" width="16" height="2" /><rect x="2" y="2" width="4" height="12" /><rect x="10" y="4" width="4" height="8" /></svg>
      </button>
      <button onClick={() => align('bottom')} className={btnCls} title="Align Bottom">
        <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="currentColor"><rect x="0" y="14" width="16" height="2" /><rect x="2" y="4" width="4" height="8" /><rect x="10" y="0" width="4" height="12" /></svg>
      </button>
      <div className="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-0.5" />
      <button onClick={() => distribute('horizontal')} className={btnCls} title="Distribute Horizontal">
        <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="currentColor"><rect x="0" y="0" width="2" height="16" /><rect x="7" y="3" width="2" height="10" /><rect x="14" y="0" width="2" height="16" /></svg>
      </button>
      <button onClick={() => distribute('vertical')} className={btnCls} title="Distribute Vertical">
        <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="currentColor"><rect x="0" y="0" width="16" height="2" /><rect x="3" y="7" width="10" height="2" /><rect x="0" y="14" width="16" height="2" /></svg>
      </button>
      <div className="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-0.5" />
      <button onClick={() => flip('h')} className={btnCls} title="Flip Horizontal">
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10M12 3v18M5 12l-3-3m0 0l3-3m-3 3h8m4-3l3 3m0 0l-3 3m3-3h-8" /></svg>
      </button>
      <button onClick={() => flip('v')} className={btnCls} title="Flip Vertical">
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ transform: 'rotate(90deg)' }}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10M12 3v18M5 12l-3-3m0 0l3-3m-3 3h8m4-3l3 3m0 0l-3 3m3-3h-8" /></svg>
      </button>
    </div>
  )
}


// =============================================================================
// CANVAS EDITOR (MAIN COMPONENT)
// =============================================================================
const CanvasEditor = forwardRef(({
  onCanvasReady, onSelectionChange, onLayersChange, onHistoryChange, onColorsExtracted, className = '',
}, ref) => {
  const canvasRef = useRef(null)
  const canvasElRef = useRef(null)
  const containerRef = useRef(null)
  const fileInputRef = useRef(null)
  const fabricReady = useRef(false)

  const [isReady, setIsReady] = useState(false)
  const [activeTool, setActiveTool] = useState('select')
  const [layers, setLayers] = useState([])
  const [activeObjectId, setActiveObjectId] = useState(null)
  const [activeObject, setActiveObject] = useState(null)

  // Text properties bar state
  const [fontSearch, setFontSearch] = useState('')
  const [activeFontCategory, setActiveFontCategory] = useState(null)
  const [fontResults, setFontResults] = useState([])
  const [showFontDropdown, setShowFontDropdown] = useState(false)
  const fontTimerRef = useRef(null)
  const fontCacheRef = useRef(null) // cache the full Google Fonts list
  const [zoom, setZoom] = useState(1)
  const [showGrid, setShowGrid] = useState(false)

  // Inpainting mask state
  const [maskMode, setMaskMode] = useState(false)
  const [maskBrushSize, setMaskBrushSize] = useState(40)
  const [maskPrompt, setMaskPrompt] = useState('')
  const [maskSubmitting, setMaskSubmitting] = useState(false)
  const maskCanvasRef = useRef(null)

  // Size the mask canvas to match the Fabric canvas when mask mode activates
  useEffect(() => {
    if (maskMode && maskCanvasRef.current && canvasRef.current) {
      const w = canvasRef.current.width
      const h = canvasRef.current.height
      maskCanvasRef.current.width = w
      maskCanvasRef.current.height = h
      // Clear to transparent
      const ctx = maskCanvasRef.current.getContext('2d')
      ctx.clearRect(0, 0, w, h)
    }
  }, [maskMode])

  // Export mask as black/white PNG for inpainting API
  const exportMaskAsDataUrl = () => {
    const mc = maskCanvasRef.current
    if (!mc) return null
    const w = mc.width
    const h = mc.height
    // Create a new canvas with black background, white where user painted
    const exportCanvas = document.createElement('canvas')
    exportCanvas.width = w
    exportCanvas.height = h
    const ctx = exportCanvas.getContext('2d')
    // Black background (keep these pixels)
    ctx.fillStyle = '#000000'
    ctx.fillRect(0, 0, w, h)
    // Get the mask pixels
    const maskCtx = mc.getContext('2d')
    const imgData = maskCtx.getImageData(0, 0, w, h)
    const outData = ctx.getImageData(0, 0, w, h)
    for (let i = 0; i < imgData.data.length; i += 4) {
      // Where the red channel is painted (user brushed), make white (regenerate)
      if (imgData.data[i] > 100) { // red channel > 100
        outData.data[i] = 255     // R
        outData.data[i + 1] = 255 // G
        outData.data[i + 2] = 255 // B
        outData.data[i + 3] = 255 // A
      }
    }
    ctx.putImageData(outData, 0, 0)
    return exportCanvas.toDataURL('image/png')
  }

  // Submit inpainting request
  const handleInpaintSubmit = async () => {
    if (!maskPrompt.trim() || maskSubmitting) return
    const maskDataUrl = exportMaskAsDataUrl()
    if (!maskDataUrl) return alert('Please paint over the area you want to change first.')

    // Get the current canvas image
    const canvasImageUrl = canvasRef.current?.toDataURL('image/png')
    if (!canvasImageUrl) return alert('No image on canvas to edit.')

    setMaskSubmitting(true)
    try {
      const API_URL = import.meta.env.VITE_API_URL || ''
      const res = await fetch(`${API_URL}/export/inpaint`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageUrl: canvasImageUrl,
          maskUrl: maskDataUrl,
          prompt: maskPrompt,
        })
      })
      const data = await res.json()
      if (data.success && data.url) {
        // Load result onto canvas
        await addImage(data.url)
        setMaskMode(false)
        setMaskPrompt('')
        alert('Region updated! The edited image has been added to your canvas.')
      } else {
        alert('Inpainting failed: ' + (data.error || 'Unknown error'))
      }
    } catch (e) {
      console.error('Inpaint error:', e)
      alert('Failed to process. Try again.')
    } finally {
      setMaskSubmitting(false)
    }
  }
  const [snapEnabled, setSnapEnabled] = useState(true)
  const [showLayers, setShowLayers] = useState(() => typeof window !== 'undefined' ? window.innerWidth >= 768 : true)
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 })
  const [showShapeMenu, setShowShapeMenu] = useState(false)
  const [showShapeLibrary, setShowShapeLibrary] = useState(false)
  const [showAlignTools, setShowAlignTools] = useState(false)
  const [fillColor, setFillColor] = useState('#8B5CF6')
  const [strokeColor, setStrokeColor] = useState('transparent')
  const [strokeWidth, setStrokeWidth] = useState(0)
  const [showColorPicker, setShowColorPicker] = useState(null)

  // Pen tool state (V1.3)
  const [penPoints, setPenPoints] = useState([])
  const [penMode, setPenMode] = useState('bezier') // 'bezier' | 'freehand'
  const penPreviewRef = useRef(null)
  const penHandlesRef = useRef([]) // control handle visualization circles

  // Node editing state (V1.3)
  const [nodeEditTarget, setNodeEditTarget] = useState(null) // path object being node-edited
  const [nodeAnchors, setNodeAnchors] = useState([])
  const nodeMarkersRef = useRef([]) // fabric circles for anchor/control points

  // Path operations (V1.3)
  const [showPathOps, setShowPathOps] = useState(false)
  const [offsetAmount, setOffsetAmount] = useState(10)
  const [textOnPathInput, setTextOnPathInput] = useState('')
  const [textOnPathFontSize, setTextOnPathFontSize] = useState(24)

  const historyRef = useRef([])
  const historyIndexRef = useRef(-1)
  const isUndoRedoRef = useRef(false)
  const clipboardRef = useRef(null)
  const layerCountRef = useRef(0)

  // Smart snap guides (V2.0)
  const snapGuidesRef = useRef([])

  // ==========================================================================
  // INIT
  // ==========================================================================
  useEffect(() => {
    if (fabricReady.current) return
    const tryInit = () => {
      const f = getFabric()
      if (!f || !canvasElRef.current || !containerRef.current) return false
      const rect = containerRef.current.getBoundingClientRect()
      const canvas = new f.Canvas(canvasElRef.current, {
        width: rect.width - (showLayers ? 224 : 0), height: rect.height,
        backgroundColor: '#ffffff', selection: true, preserveObjectStacking: true,
        controlsAboveOverlay: true, imageSmoothingEnabled: true,
      })
      canvas.selectionColor = 'rgba(139, 92, 246, 0.1)'
      canvas.selectionBorderColor = '#8B5CF6'
      canvas.selectionLineWidth = 1.5
      canvasRef.current = canvas
      fabricReady.current = true
      setIsReady(true)
      pushHistory()

      canvas.on('object:modified', () => { if (!isUndoRedoRef.current) pushHistory(); syncLayers() })
      canvas.on('object:added', () => { if (!isUndoRedoRef.current) pushHistory(); syncLayers() })
      canvas.on('object:removed', () => { if (!isUndoRedoRef.current) pushHistory(); syncLayers() })
      canvas.on('selection:created', (e) => { const o = e.selected?.[0]; if (o) { setActiveObjectId(o._layerId); setActiveObject(o) }; onSelectionChange?.(o) })
      canvas.on('selection:updated', (e) => { const o = e.selected?.[0]; if (o) { setActiveObjectId(o._layerId); setActiveObject(o) }; onSelectionChange?.(o) })
      canvas.on('selection:cleared', () => { setActiveObjectId(null); setActiveObject(null); onSelectionChange?.(null) })

      // Snap to grid
      canvas.on('object:moving', (e) => {
        if (!snapEnabled || !showGrid) return
        const obj = e.target
        obj.set({ left: Math.round(obj.left / GRID_SIZE) * GRID_SIZE, top: Math.round(obj.top / GRID_SIZE) * GRID_SIZE })
      })

      // Smart snap alignment guides (V2.0)
      canvas.on('object:moving', (e) => {
        if (!snapEnabled) return
        clearSnapGuides()
        const moving = e.target
        const movingBound = moving.getBoundingRect()
        const cx = movingBound.left + movingBound.width / 2
        const cy = movingBound.top + movingBound.height / 2
        const canvasCx = canvas.width / 2, canvasCy = canvas.height / 2

        // Snap to canvas center
        if (Math.abs(cx - canvasCx) < SNAP_THRESHOLD) {
          moving.set({ left: moving.left + (canvasCx - cx) })
          drawSnapGuide(canvasCx, 0, canvasCx, canvas.height, 'v')
        }
        if (Math.abs(cy - canvasCy) < SNAP_THRESHOLD) {
          moving.set({ top: moving.top + (canvasCy - cy) })
          drawSnapGuide(0, canvasCy, canvas.width, canvasCy, 'h')
        }

        // Snap to other objects
        canvas.getObjects().forEach(obj => {
          if (obj === moving || obj._isGrid || obj._isSnapGuide) return
          const b = obj.getBoundingRect()
          const ocx = b.left + b.width / 2, ocy = b.top + b.height / 2
          if (Math.abs(cx - ocx) < SNAP_THRESHOLD) {
            moving.set({ left: moving.left + (ocx - cx) })
            drawSnapGuide(ocx, Math.min(movingBound.top, b.top), ocx, Math.max(movingBound.top + movingBound.height, b.top + b.height), 'v')
          }
          if (Math.abs(cy - ocy) < SNAP_THRESHOLD) {
            moving.set({ top: moving.top + (ocy - cy) })
            drawSnapGuide(Math.min(movingBound.left, b.left), ocy, Math.max(movingBound.left + movingBound.width, b.left + b.width), ocy, 'h')
          }
        })
      })

      canvas.on('object:moved', clearSnapGuides)

      // Mouse wheel zoom
      canvas.on('mouse:wheel', (opt) => {
        const delta = opt.e.deltaY
        let nz = canvas.getZoom() * (delta > 0 ? 0.95 : 1.05)
        nz = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, nz))
        canvas.zoomToPoint({ x: opt.e.offsetX, y: opt.e.offsetY }, nz)
        setZoom(nz)
        opt.e.preventDefault(); opt.e.stopPropagation()
      })

      // ─── TOUCH GESTURES: pinch-to-zoom + two-finger pan ───────────────────
      // Tracks the last distance between two touches. When the user pinches,
      // we scale the canvas zoom by the distance delta. Two-finger drag pans.
      const canvasEl = canvas.getElement()
      const upperEl = canvas.upperCanvasEl || canvasEl
      let lastTouchDistance = 0
      let lastTouchCenter = null

      const getTouchDistance = (e) => {
        const dx = e.touches[0].clientX - e.touches[1].clientX
        const dy = e.touches[0].clientY - e.touches[1].clientY
        return Math.sqrt(dx * dx + dy * dy)
      }
      const getTouchCenter = (e) => {
        const rect = upperEl.getBoundingClientRect()
        return {
          x: (e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left,
          y: (e.touches[0].clientY + e.touches[1].clientY) / 2 - rect.top,
        }
      }

      const onTouchStart = (e) => {
        if (e.touches.length === 2) {
          // Start pinch — disable object selection during gesture
          canvas.selection = false
          canvas.discardActiveObject()
          canvas.skipTargetFind = true
          lastTouchDistance = getTouchDistance(e)
          lastTouchCenter = getTouchCenter(e)
          e.preventDefault()
        }
      }
      const onTouchMove = (e) => {
        if (e.touches.length === 2 && lastTouchDistance > 0) {
          const newDistance = getTouchDistance(e)
          const newCenter = getTouchCenter(e)
          // Zoom component
          const scale = newDistance / lastTouchDistance
          let nz = canvas.getZoom() * scale
          nz = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, nz))
          canvas.zoomToPoint(newCenter, nz)
          // Pan component (delta in center of two fingers)
          if (lastTouchCenter) {
            const dx = newCenter.x - lastTouchCenter.x
            const dy = newCenter.y - lastTouchCenter.y
            const vpt = canvas.viewportTransform
            vpt[4] += dx
            vpt[5] += dy
            canvas.setViewportTransform(vpt)
          }
          setZoom(nz)
          lastTouchDistance = newDistance
          lastTouchCenter = newCenter
          e.preventDefault()
        }
      }
      const onTouchEnd = (e) => {
        if (e.touches.length < 2) {
          // Pinch ended — re-enable object selection
          canvas.selection = true
          canvas.skipTargetFind = false
          lastTouchDistance = 0
          lastTouchCenter = null
        }
      }

      upperEl.addEventListener('touchstart', onTouchStart, { passive: false })
      upperEl.addEventListener('touchmove', onTouchMove, { passive: false })
      upperEl.addEventListener('touchend', onTouchEnd)
      upperEl.addEventListener('touchcancel', onTouchEnd)

      // Stash for cleanup
      canvas._touchCleanup = () => {
        upperEl.removeEventListener('touchstart', onTouchStart)
        upperEl.removeEventListener('touchmove', onTouchMove)
        upperEl.removeEventListener('touchend', onTouchEnd)
        upperEl.removeEventListener('touchcancel', onTouchEnd)
      }

      // Pen tool click handler
      canvas.on('mouse:down', (opt) => {
        if (activeTool !== 'pen') return
        const pointer = canvas.getPointer(opt.e)
        handlePenClick(pointer)
      })

      onCanvasReady?.(canvas)
      return true
    }
    if (!tryInit()) {
      const interval = setInterval(() => { if (tryInit()) clearInterval(interval) }, 100)
      return () => clearInterval(interval)
    }
    return () => {
      if (canvasRef.current) {
        if (canvasRef.current._touchCleanup) canvasRef.current._touchCleanup()
        canvasRef.current.dispose()
        canvasRef.current = null
        fabricReady.current = false
      }
    }
  }, [])

  // Resize observer
  useEffect(() => {
    if (!containerRef.current || !canvasRef.current) return
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry || !canvasRef.current) return
      // Skip if container is hidden (tab switched to 3D) — don't shrink canvas to 0
      if (entry.contentRect.width === 0 || entry.contentRect.height === 0) return
      const lw = showLayers ? 224 : 0
      canvasRef.current.setDimensions({ width: Math.max(400, entry.contentRect.width - lw), height: Math.max(300, entry.contentRect.height) })
      canvasRef.current.renderAll()
    })
    observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [showLayers])

  useEffect(() => {
    if (!canvasRef.current || !containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    // Skip if container is hidden
    if (rect.width === 0 || rect.height === 0) return
    const lw = showLayers ? 224 : 0
    canvasRef.current.setDimensions({ width: Math.max(400, rect.width - lw), height: Math.max(300, rect.height) })
    canvasRef.current.renderAll()
  }, [showLayers])

  // ==========================================================================
  // SMART SNAP GUIDES (V2.0)
  // ==========================================================================
  const drawSnapGuide = useCallback((x1, y1, x2, y2) => {
    const f = getFabric(); const canvas = canvasRef.current; if (!f || !canvas) return
    const line = new f.Line([x1, y1, x2, y2], { stroke: '#8B5CF6', strokeWidth: 1, strokeDashArray: [4, 4], selectable: false, evented: false, _isSnapGuide: true, _isGrid: true })
    canvas.add(line)
    snapGuidesRef.current.push(line)
  }, [])

  const clearSnapGuides = useCallback(() => {
    const canvas = canvasRef.current; if (!canvas) return
    snapGuidesRef.current.forEach(g => canvas.remove(g))
    snapGuidesRef.current = []
    canvas.renderAll()
  }, [])

  // ==========================================================================
  // KEYBOARD SHORTCUTS
  // ==========================================================================
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!canvasRef.current) return
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return
      const ctrl = e.ctrlKey || e.metaKey

      if (ctrl && !e.shiftKey && e.key === 'z') { e.preventDefault(); undo() }
      else if ((ctrl && e.shiftKey && e.key === 'z') || (ctrl && e.key === 'y')) { e.preventDefault(); redo() }
      else if (ctrl && e.key === 'c') { e.preventDefault(); copySelected() }
      else if (ctrl && e.key === 'v') { e.preventDefault(); pasteClipboard() }
      else if (ctrl && e.key === 'd') { e.preventDefault(); duplicateSelected() }
      else if (ctrl && e.key === 'a') { e.preventDefault(); selectAll() }
      else if (ctrl && e.key === 'g') { e.preventDefault(); groupSelected() }
      else if (ctrl && e.shiftKey && e.key === 'G') { e.preventDefault(); ungroupSelected() }
      else if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); deleteSelected() }
      else if (e.key === 'Escape') {
        canvasRef.current.discardActiveObject(); canvasRef.current.renderAll()
        if (activeTool === 'pen') finalizePenPath()
        if (activeTool === 'node-edit') exitNodeEditMode()
        setActiveTool('select'); setShowShapeMenu(false); setShowColorPicker(null); setShowShapeLibrary(false); setShowPathOps(false)
      }
      else if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        const obj = canvasRef.current.getActiveObject()
        if (!obj || obj.lockMovementX) return
        e.preventDefault()
        const step = e.shiftKey ? 10 : 1
        if (e.key === 'ArrowUp') obj.top -= step
        else if (e.key === 'ArrowDown') obj.top += step
        else if (e.key === 'ArrowLeft') obj.left -= step
        else if (e.key === 'ArrowRight') obj.left += step
        obj.setCoords(); canvasRef.current.renderAll(); pushHistory()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [activeTool])

  // ==========================================================================
  // HISTORY
  // ==========================================================================
  const pushHistory = useCallback(() => {
    if (!canvasRef.current) return
    const json = canvasRef.current.toJSON(['_layerId', '_layerName', '_layerLocked'])
    if (historyIndexRef.current < historyRef.current.length - 1)
      historyRef.current = historyRef.current.slice(0, historyIndexRef.current + 1)
    historyRef.current.push(JSON.stringify(json))
    historyIndexRef.current = historyRef.current.length - 1
    if (historyRef.current.length > HISTORY_LIMIT) { historyRef.current.shift(); historyIndexRef.current-- }
    onHistoryChange?.({ canUndo: historyIndexRef.current > 0, canRedo: historyIndexRef.current < historyRef.current.length - 1 })
  }, [onHistoryChange])

  const restoreHistory = useCallback((index) => {
    if (!canvasRef.current || index < 0 || index >= historyRef.current.length) return
    isUndoRedoRef.current = true
    canvasRef.current.loadFromJSON(JSON.parse(historyRef.current[index]), () => {
      canvasRef.current.renderAll(); syncLayers(); isUndoRedoRef.current = false
      onHistoryChange?.({ canUndo: index > 0, canRedo: index < historyRef.current.length - 1 })
    })
  }, [onHistoryChange])

  const undo = useCallback(() => { if (historyIndexRef.current <= 0) return; historyIndexRef.current--; restoreHistory(historyIndexRef.current) }, [restoreHistory])
  const redo = useCallback(() => { if (historyIndexRef.current >= historyRef.current.length - 1) return; historyIndexRef.current++; restoreHistory(historyIndexRef.current) }, [restoreHistory])

  // ==========================================================================
  // LAYERS
  // ==========================================================================
  const syncLayers = useCallback(() => {
    if (!canvasRef.current) return
    const layerData = canvasRef.current.getObjects()
      .filter(obj => !obj._isGrid && !obj._isSnapGuide)
      .map((obj) => ({ id: obj._layerId, name: obj._layerName || obj.type || 'Object', type: obj.type, visible: obj.visible !== false, locked: !!obj._layerLocked }))
    setLayers(layerData)
    onLayersChange?.(layerData)
  }, [onLayersChange])

  const getObjectById = (id) => canvasRef.current?.getObjects().find(obj => obj._layerId === id)
  const selectLayer = (id) => { const obj = getObjectById(id); if (obj && !obj._layerLocked) { canvasRef.current.setActiveObject(obj); canvasRef.current.renderAll() }; setActiveObjectId(id); setActiveObject(obj || null) }
  const toggleLayerVisibility = (id) => { const o = getObjectById(id); if (!o) return; o.visible = !o.visible; canvasRef.current.renderAll(); syncLayers(); pushHistory() }
  const toggleLayerLock = (id) => {
    const o = getObjectById(id); if (!o) return
    const locked = !o._layerLocked; o._layerLocked = locked
    o.lockMovementX = locked; o.lockMovementY = locked; o.lockRotation = locked; o.lockScalingX = locked; o.lockScalingY = locked
    o.selectable = !locked; o.evented = !locked
    canvasRef.current.renderAll(); syncLayers()
  }
  const reorderLayer = (id, dir) => { const o = getObjectById(id); if (!o) return; if (dir === 'up') canvasRef.current.bringForward(o); else canvasRef.current.sendBackwards(o); canvasRef.current.renderAll(); syncLayers(); pushHistory() }
  const deleteLayer = (id) => { const o = getObjectById(id); if (o) canvasRef.current.remove(o); canvasRef.current.renderAll() }
  const renameLayer = (id, name) => { const o = getObjectById(id); if (o) o._layerName = name; syncLayers() }

  // ==========================================================================
  // LAYER GROUPS (V1.3)
  // ==========================================================================
  const groupSelected = useCallback(() => {
    const canvas = canvasRef.current; const f = getFabric(); if (!canvas || !f) return
    const active = canvas.getActiveObject()
    if (!active || active.type !== 'activeSelection') return
    const group = active.toGroup()
    assignLayerId(group, `Group ${layerCountRef.current + 1}`)
    canvas.renderAll(); syncLayers(); pushHistory()
  }, [])

  const ungroupSelected = useCallback(() => {
    const canvas = canvasRef.current; if (!canvas) return
    const active = canvas.getActiveObject()
    if (!active || active.type !== 'group') return
    const items = active.toActiveSelection()
    items.getObjects().forEach(obj => { if (!obj._layerId) assignLayerId(obj) })
    canvas.renderAll(); syncLayers(); pushHistory()
  }, [])

  // ==========================================================================
  // BOOLEAN OPERATIONS (V1.3)
  // ==========================================================================
  const booleanOp = useCallback((operation) => {
    const canvas = canvasRef.current; const f = getFabric(); if (!canvas || !f) return
    const active = canvas.getActiveObject()
    if (!active || active.type !== 'activeSelection') return
    const objects = active.getObjects()
    if (objects.length < 2) return

    // For boolean ops we need SVG path data - use clipPath approach
    const base = objects[0]; const tool = objects[1]
    canvas.discardActiveObject()

    if (operation === 'union') {
      // Union: group and flatten into single visual
      const group = new f.Group([base, tool].map(o => { canvas.remove(o); return o }))
      assignLayerId(group, `Union ${layerCountRef.current + 1}`)
      canvas.add(group); canvas.setActiveObject(group)
    } else if (operation === 'intersect') {
      // Intersect: use clipPath
      canvas.remove(tool)
      const clone = fabric.util.object.clone(tool)
      clone.set({ absolutePositioned: true })
      base.clipPath = clone
      canvas.renderAll()
    } else if (operation === 'subtract') {
      // Subtract: use inverted clipPath
      canvas.remove(tool)
      const clone = fabric.util.object.clone(tool)
      clone.set({ absolutePositioned: true, inverted: true })
      base.clipPath = clone
      canvas.renderAll()
    }

    canvas.renderAll(); syncLayers(); pushHistory()
  }, [])

  // ==========================================================================
  // PEN TOOL (V1.3) — Bezier path drawing
  // ==========================================================================
  const handlePenClick = useCallback((pointer) => {
    setPenPoints(prev => {
      const newPoints = [...prev, { x: pointer.x, y: pointer.y }]
      drawPenPreview(newPoints)
      return newPoints
    })
  }, [fillColor, strokeColor])

  const drawPenPreview = useCallback((points) => {
    const f = getFabric(); const canvas = canvasRef.current; if (!f || !canvas || points.length < 2) return
    // Remove existing preview
    if (penPreviewRef.current) canvas.remove(penPreviewRef.current)
    // Build SVG path string from points (smooth bezier through points)
    let pathStr = `M ${points[0].x} ${points[0].y}`
    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1], curr = points[i]
      const cpx = (prev.x + curr.x) / 2, cpy = (prev.y + curr.y) / 2
      pathStr += ` Q ${prev.x} ${prev.y} ${cpx} ${cpy}`
    }
    const last = points[points.length - 1]
    pathStr += ` L ${last.x} ${last.y}`

    const preview = new f.Path(pathStr, { fill: 'transparent', stroke: '#8B5CF6', strokeWidth: 2, strokeDashArray: [5, 5], selectable: false, evented: false, _isGrid: true })
    canvas.add(preview)
    penPreviewRef.current = preview
    canvas.renderAll()
  }, [])

  const finalizePenPath = useCallback(() => {
    const f = getFabric(); const canvas = canvasRef.current; if (!f || !canvas) return
    if (penPreviewRef.current) canvas.remove(penPreviewRef.current)
    penPreviewRef.current = null

    if (penPoints.length < 2) { setPenPoints([]); return }

    let pathStr = `M ${penPoints[0].x} ${penPoints[0].y}`
    for (let i = 1; i < penPoints.length; i++) {
      const prev = penPoints[i - 1], curr = penPoints[i]
      const cpx = (prev.x + curr.x) / 2, cpy = (prev.y + curr.y) / 2
      pathStr += ` Q ${prev.x} ${prev.y} ${cpx} ${cpy}`
    }
    const last = penPoints[penPoints.length - 1]
    pathStr += ` L ${last.x} ${last.y}`

    const path = new f.Path(pathStr, {
      fill: fillColor, stroke: strokeColor !== 'transparent' ? strokeColor : '#0F172A',
      strokeWidth: strokeWidth || 2, originX: 'center', originY: 'center',
    })
    assignLayerId(path, `Pen Path ${layerCountRef.current + 1}`)
    canvas.add(path); canvas.setActiveObject(path); canvas.renderAll()
    setPenPoints([]); setActiveTool('select'); pushHistory(); syncLayers()
  }, [penPoints, fillColor, strokeColor, strokeWidth])

  // ==========================================================================
  // NODE EDITING MODE (V1.3)
  // ==========================================================================
  const enterNodeEditMode = useCallback(() => {
    const canvas = canvasRef.current; if (!canvas) return
    const active = canvas.getActiveObject()
    if (!active || active.type !== 'path') return
    setActiveTool('node-edit')
    setNodeEditTarget(active)
    active.selectable = false; active.evented = false

    // Render anchor points and control handles
    const anchors = getPathAnchors(active)
    setNodeAnchors(anchors)
    renderNodeMarkers(anchors, active)
  }, [])

  const exitNodeEditMode = useCallback(() => {
    const canvas = canvasRef.current; if (!canvas) return
    // Clean up markers
    nodeMarkersRef.current.forEach(m => canvas.remove(m))
    nodeMarkersRef.current = []
    if (nodeEditTarget) {
      nodeEditTarget.selectable = true; nodeEditTarget.evented = true
      nodeEditTarget.set({ dirty: true }); nodeEditTarget.setCoords()
    }
    setNodeEditTarget(null); setNodeAnchors([]); setActiveTool('select')
    canvas.renderAll(); pushHistory()
  }, [nodeEditTarget, pushHistory])

  const renderNodeMarkers = useCallback((anchors, pathObj) => {
    const f = getFabric(); const canvas = canvasRef.current; if (!f || !canvas) return
    // Clear existing markers
    nodeMarkersRef.current.forEach(m => canvas.remove(m))
    nodeMarkersRef.current = []

    anchors.forEach((anchor, i) => {
      const isControl = anchor.type === 'control'
      const marker = new f.Circle({
        radius: isControl ? 4 : 5,
        fill: isControl ? '#06B6D4' : '#8B5CF6',
        stroke: '#fff',
        strokeWidth: 1.5,
        selectable: true,
        evented: true,
        hasControls: false,
        hasBorders: false,
        originX: 'center',
        originY: 'center',
        left: anchor.x,
        top: anchor.y,
        _isNodeMarker: true,
        _isGrid: true,
        _anchorIdx: i,
      })

      // Drag handler: update path anchor position
      marker.on('moving', () => {
        const dx = marker.left - anchor.x
        const dy = marker.top - anchor.y
        updatePathAnchor(pathObj, anchor.idx, anchor.pathIdx, dx, dy)
        anchor.x = marker.left
        anchor.y = marker.top
      })

      marker.on('moved', () => {
        // Re-render all markers with updated positions
        const newAnchors = getPathAnchors(pathObj)
        setNodeAnchors(newAnchors)
        renderNodeMarkers(newAnchors, pathObj)
      })

      canvas.add(marker)
      nodeMarkersRef.current.push(marker)

      // Draw lines between control handles and their anchor points
      if (isControl && i > 0) {
        const prevAnchor = anchors[i - 1]
        if (prevAnchor) {
          const line = new f.Line([anchor.x, anchor.y, prevAnchor.x, prevAnchor.y], {
            stroke: '#06B6D4', strokeWidth: 0.8, strokeDashArray: [3, 3],
            selectable: false, evented: false, _isGrid: true, _isNodeMarker: true,
          })
          canvas.add(line); canvas.sendToBack(line)
          nodeMarkersRef.current.push(line)
        }
      }
    })
    canvas.renderAll()
  }, [])

  const addNodeToPath = useCallback(() => {
    if (!nodeEditTarget?.path) return
    const path = nodeEditTarget.path
    // Find midpoint of last segment and insert a new L point
    if (path.length >= 2) {
      const last = path[path.length - 1]
      const prev = path[path.length - 2]
      const cmd = last[0]
      let mx, my
      if (cmd === 'L') { mx = (prev[prev.length - 2] + last[1]) / 2; my = (prev[prev.length - 1] + last[2]) / 2 }
      else if (cmd === 'Q') { mx = last[1]; my = last[2] } // use control point as new anchor
      else if (cmd === 'C') { mx = last[3]; my = last[4] }
      else return
      // Insert new line point before last segment
      path.splice(path.length - 1, 0, ['L', mx, my])
      nodeEditTarget.set({ dirty: true })
      const anchors = getPathAnchors(nodeEditTarget)
      setNodeAnchors(anchors)
      renderNodeMarkers(anchors, nodeEditTarget)
    }
  }, [nodeEditTarget, renderNodeMarkers])

  const deleteNodeFromPath = useCallback(() => {
    if (!nodeEditTarget?.path || nodeEditTarget.path.length <= 2) return
    // Remove last non-M, non-Z segment
    const path = nodeEditTarget.path
    for (let i = path.length - 1; i >= 0; i--) {
      if (path[i][0] !== 'M' && path[i][0] !== 'Z') {
        path.splice(i, 1)
        break
      }
    }
    nodeEditTarget.set({ dirty: true })
    const anchors = getPathAnchors(nodeEditTarget)
    setNodeAnchors(anchors)
    renderNodeMarkers(anchors, nodeEditTarget)
  }, [nodeEditTarget, renderNodeMarkers])

  // ==========================================================================
  // STROKE TO PATH (V1.3)
  // ==========================================================================
  const handleStrokeToPath = useCallback(() => {
    const canvas = canvasRef.current; if (!canvas) return
    const active = canvas.getActiveObject(); if (!active) return
    strokeToPath(active, canvas)
    syncLayers(); pushHistory()
  }, [syncLayers, pushHistory])

  // ==========================================================================
  // PATH OFFSET / INSET (V1.3)
  // ==========================================================================
  const handlePathOffset = useCallback((amount) => {
    const canvas = canvasRef.current; if (!canvas) return
    const active = canvas.getActiveObject(); if (!active) return
    pathOffset(active, canvas, amount)
    syncLayers(); pushHistory()
  }, [syncLayers, pushHistory])

  // ==========================================================================
  // TEXT ON PATH (V1.3)
  // ==========================================================================
  const handleTextOnPath = useCallback(() => {
    const canvas = canvasRef.current; if (!canvas) return
    const active = canvas.getActiveObject()
    if (!active || active.type !== 'path') return
    if (!textOnPathInput.trim()) return
    textOnPath(textOnPathInput.trim(), active, canvas, {
      fontSize: textOnPathFontSize,
      fontFamily: 'Arial',
      fill: fillColor,
    })
    setTextOnPathInput('')
    syncLayers(); pushHistory()
  }, [textOnPathInput, textOnPathFontSize, fillColor, syncLayers, pushHistory])

  // ==========================================================================
  // MIRROR / SYMMETRY (V2.0)
  // ==========================================================================
  const mirrorDesign = useCallback((axis) => {
    const canvas = canvasRef.current; const f = getFabric(); if (!canvas || !f) return
    const objects = canvas.getObjects().filter(o => !o._isGrid && !o._isSnapGuide)
    objects.forEach(obj => {
      obj.clone((cloned) => {
        if (axis === 'horizontal') {
          cloned.set({ left: canvas.width - cloned.left - cloned.width * cloned.scaleX, flipX: !cloned.flipX })
        } else {
          cloned.set({ top: canvas.height - cloned.top - cloned.height * cloned.scaleY, flipY: !cloned.flipY })
        }
        assignLayerId(cloned, `Mirror ${layerCountRef.current + 1}`)
        canvas.add(cloned)
      })
    })
    canvas.renderAll(); pushHistory(); syncLayers()
  }, [])

  // ==========================================================================
  // TILE / REPEAT PATTERN (V2.0)
  // ==========================================================================
  const tilePattern = useCallback((cols = 3, rows = 3) => {
    const canvas = canvasRef.current; const f = getFabric(); if (!canvas || !f) return
    const active = canvas.getActiveObject(); if (!active) return
    const bound = active.getBoundingRect()
    const w = bound.width + 5, h = bound.height + 5
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (r === 0 && c === 0) continue
        active.clone((cloned) => {
          cloned.set({ left: active.left + c * w, top: active.top + r * h })
          assignLayerId(cloned, `Tile ${layerCountRef.current + 1}`)
          canvas.add(cloned)
        })
      }
    }
    canvas.renderAll(); pushHistory(); syncLayers()
  }, [])

  // ==========================================================================
  // TEXTURE & MATERIAL PREVIEW (V2.0)
  // ==========================================================================
  const TEXTURES = {
    fabric: {
      label: 'Fabric',
      items: [
        { id: 'cotton', name: 'Cotton Weave', pattern: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.03) 2px, rgba(0,0,0,0.03) 4px), repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(0,0,0,0.03) 2px, rgba(0,0,0,0.03) 4px)', opacity: 0.4 },
        { id: 'polyester', name: 'Polyester', pattern: 'repeating-linear-gradient(45deg, transparent, transparent 1px, rgba(0,0,0,0.02) 1px, rgba(0,0,0,0.02) 3px)', opacity: 0.3 },
        { id: 'denim', name: 'Denim Twill', pattern: 'repeating-linear-gradient(135deg, transparent, transparent 2px, rgba(29,78,137,0.08) 2px, rgba(29,78,137,0.08) 4px)', opacity: 0.5 },
        { id: 'canvas-fabric', name: 'Canvas', pattern: 'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(139,119,80,0.06) 3px, rgba(139,119,80,0.06) 5px), repeating-linear-gradient(90deg, transparent, transparent 3px, rgba(139,119,80,0.06) 3px, rgba(139,119,80,0.06) 5px)', opacity: 0.4 },
        { id: 'felt', name: 'Felt', pattern: 'radial-gradient(circle at 30% 30%, rgba(0,0,0,0.03) 1px, transparent 1px)', opacity: 0.5 },
        { id: 'fleece', name: 'Fleece', pattern: 'repeating-linear-gradient(30deg, transparent, transparent 1px, rgba(0,0,0,0.015) 1px, rgba(0,0,0,0.015) 2px)', opacity: 0.3 },
        { id: 'satin', name: 'Satin', pattern: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 40%, rgba(255,255,255,0.05) 60%, transparent 100%)', opacity: 0.3 },
      ]
    },
    paper: {
      label: 'Paper',
      items: [
        { id: 'cardstock', name: 'Cardstock', pattern: 'none', opacity: 0, bg: '#faf8f5' },
        { id: 'kraft', name: 'Kraft Paper', pattern: 'radial-gradient(circle, rgba(101,67,33,0.06) 1px, transparent 1px)', opacity: 0.5, bg: '#c4a882' },
        { id: 'glossy', name: 'Glossy Photo', pattern: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, transparent 50%, rgba(255,255,255,0.08) 100%)', opacity: 0.3 },
        { id: 'watercolor', name: 'Watercolor', pattern: 'radial-gradient(circle, rgba(0,0,0,0.02) 2px, transparent 2px)', opacity: 0.4, bg: '#fefcf8' },
        { id: 'vellum', name: 'Vellum', pattern: 'none', opacity: 0.15, bg: '#fffef5' },
      ]
    },
    effects: {
      label: 'Effects',
      items: [
        { id: 'glitter', name: 'Glitter', pattern: 'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.6) 0.5px, transparent 0.5px), radial-gradient(circle at 70% 40%, rgba(255,215,0,0.4) 0.3px, transparent 0.3px), radial-gradient(circle at 40% 80%, rgba(255,255,255,0.5) 0.4px, transparent 0.4px)', opacity: 0.6 },
        { id: 'holographic', name: 'Holographic', pattern: 'linear-gradient(135deg, rgba(255,0,100,0.1), rgba(0,255,200,0.1), rgba(100,0,255,0.1), rgba(255,200,0,0.1))', opacity: 0.4 },
        { id: 'foil', name: 'Foil (Gold)', pattern: 'linear-gradient(135deg, rgba(212,175,55,0.2) 0%, rgba(255,215,0,0.1) 50%, rgba(212,175,55,0.2) 100%)', opacity: 0.5 },
        { id: 'metallic', name: 'Metallic', pattern: 'linear-gradient(160deg, rgba(192,192,192,0.1) 0%, rgba(255,255,255,0.15) 30%, rgba(192,192,192,0.05) 60%, rgba(255,255,255,0.1) 100%)', opacity: 0.4 },
      ]
    }
  }

  const [activeTexture, setActiveTexture] = useState(null)
  const [showTexturePanel, setShowTexturePanel] = useState(false)

  const applyTexture = useCallback((texture) => {
    const canvas = canvasRef.current; if (!canvas) return
    if (activeTexture?.id === texture.id) {
      canvas.backgroundColor = '#ffffff'; setActiveTexture(null)
    } else {
      if (texture.bg) canvas.backgroundColor = texture.bg
      setActiveTexture(texture)
    }
    canvas.renderAll()
  }, [activeTexture])

  // ==========================================================================
  // AUTO-ARRANGE LAYOUT (V2.0)
  // ==========================================================================
  const autoArrange = useCallback((mode = 'grid') => {
    const canvas = canvasRef.current; if (!canvas) return
    const objects = canvas.getObjects().filter(o => !o._isGrid && !o._isSnapGuide && o.selectable !== false)
    if (objects.length === 0) return
    const padding = 15, cw = canvas.width, ch = canvas.height

    if (mode === 'grid') {
      const cols = Math.ceil(Math.sqrt(objects.length))
      const rows = Math.ceil(objects.length / cols)
      const cellW = (cw - padding * (cols + 1)) / cols
      const cellH = (ch - padding * (rows + 1)) / rows
      objects.forEach((obj, i) => {
        const col = i % cols, row = Math.floor(i / cols)
        const bounds = obj.getBoundingRect()
        const scale = Math.min(cellW / bounds.width, cellH / bounds.height, 1) * 0.85
        obj.set({ left: padding + col * (cellW + padding) + cellW / 2, top: padding + row * (cellH + padding) + cellH / 2,
          scaleX: (obj.scaleX || 1) * scale, scaleY: (obj.scaleY || 1) * scale, originX: 'center', originY: 'center' })
        obj.setCoords()
      })
    } else if (mode === 'horizontal') {
      let x = padding
      objects.forEach(obj => {
        const bounds = obj.getBoundingRect()
        obj.set({ left: x + bounds.width / 2, top: ch / 2, originX: 'center', originY: 'center' }); obj.setCoords()
        x += bounds.width + padding
      })
    } else if (mode === 'circle') {
      const cx = cw / 2, cy = ch / 2, radius = Math.min(cw, ch) / 2 - 80
      objects.forEach((obj, i) => {
        const angle = (2 * Math.PI * i) / objects.length - Math.PI / 2
        obj.set({ left: cx + radius * Math.cos(angle), top: cy + radius * Math.sin(angle), originX: 'center', originY: 'center' }); obj.setCoords()
      })
    }
    canvas.renderAll(); pushHistory()
  }, [pushHistory])

  // ==========================================================================
  // NESTING OPTIMIZATION (V2.0) — Minimize waste for cutting
  // ==========================================================================
  const nestObjects = useCallback((sheetWidth, sheetHeight) => {
    const canvas = canvasRef.current; if (!canvas) return
    const objects = canvas.getObjects().filter(o => !o._isGrid && !o._isSnapGuide && o.selectable !== false)
    if (objects.length === 0) return
    const w = sheetWidth || canvas.width, h = sheetHeight || canvas.height, gap = 5
    const sorted = [...objects].map(obj => ({ obj, bounds: obj.getBoundingRect() }))
    sorted.sort((a, b) => b.bounds.height - a.bounds.height)
    let shelfX = gap, shelfY = gap, shelfMaxH = 0
    sorted.forEach(({ obj, bounds }) => {
      if (shelfX + bounds.width + gap > w) { shelfX = gap; shelfY += shelfMaxH + gap; shelfMaxH = 0 }
      obj.set({ left: shelfX + (obj.left - bounds.left), top: shelfY + (obj.top - bounds.top) }); obj.setCoords()
      shelfX += bounds.width + gap; shelfMaxH = Math.max(shelfMaxH, bounds.height + gap)
    })
    const totalArea = objects.reduce((s, o) => { const b = o.getBoundingRect(); return s + b.width * b.height }, 0)
    const usedH = shelfY + shelfMaxH + gap
    canvas.renderAll(); pushHistory()
    return { utilization: `${((totalArea / (w * usedH)) * 100).toFixed(1)}%`, sheets: Math.ceil(usedH / h), usedHeight: usedH }
  }, [pushHistory])

  // ==========================================================================
  // MULTI-HOOP AUTO-SPLIT (V2.0) — Split embroidery across hoops
  // ==========================================================================
  const multiHoopSplit = useCallback((hoopW = 200, hoopH = 200) => {
    const canvas = canvasRef.current; const f = getFabric(); if (!canvas || !f) return
    const objects = canvas.getObjects().filter(o => !o._isGrid && !o._isSnapGuide)
    if (objects.length === 0) return
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
    objects.forEach(o => { const b = o.getBoundingRect(); minX = Math.min(minX, b.left); minY = Math.min(minY, b.top); maxX = Math.max(maxX, b.left + b.width); maxY = Math.max(maxY, b.top + b.height) })
    const totalW = maxX - minX, totalH = maxY - minY
    const cols = Math.ceil(totalW / hoopW), rows = Math.ceil(totalH / hoopH), overlap = 10
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const x = minX + c * (hoopW - overlap), y = minY + r * (hoopH - overlap)
        canvas.add(new f.Rect({ left: x, top: y, width: hoopW, height: hoopH, fill: 'transparent', stroke: '#EC4899', strokeWidth: 1.5, strokeDashArray: [8, 4], selectable: false, evented: false, _isGrid: true }))
        canvas.add(new f.IText(`Hoop ${r * cols + c + 1}`, { left: x + 5, top: y + 5, fontSize: 11, fill: '#EC4899', fontFamily: 'Arial', selectable: false, evented: false, _isGrid: true }))
      }
    }
    canvas.renderAll()
    return { totalHoops: cols * rows, hoopSize: `${hoopW}×${hoopH}`, designSize: `${Math.round(totalW)}×${Math.round(totalH)}`, grid: `${cols}×${rows}` }
  }, [])

  // ==========================================================================
  // CLIPBOARD
  // ==========================================================================
  const copySelected = useCallback(() => { const active = canvasRef.current?.getActiveObject(); if (!active) return; active.clone((cloned) => { clipboardRef.current = cloned }) }, [])
  const pasteClipboard = useCallback(() => {
    if (!canvasRef.current || !clipboardRef.current) return
    clipboardRef.current.clone((cloned) => {
      const canvas = canvasRef.current; canvas.discardActiveObject()
      cloned.set({ left: (cloned.left || 0) + 20, top: (cloned.top || 0) + 20, evented: true })
      if (cloned.type === 'activeSelection') {
        cloned.canvas = canvas; cloned.forEachObject((obj) => { assignLayerId(obj); canvas.add(obj) }); cloned.setCoords()
      } else { assignLayerId(cloned); canvas.add(cloned) }
      clipboardRef.current.top += 20; clipboardRef.current.left += 20
      canvas.setActiveObject(cloned); canvas.requestRenderAll()
    })
  }, [])

  const duplicateSelected = useCallback(() => {
    const active = canvasRef.current?.getActiveObject(); if (!active) return
    active.clone((cloned) => {
      cloned.set({ left: (cloned.left || 0) + 20, top: (cloned.top || 0) + 20, evented: true })
      assignLayerId(cloned); canvasRef.current.add(cloned); canvasRef.current.setActiveObject(cloned); canvasRef.current.requestRenderAll()
    })
  }, [])

  const selectAll = useCallback(() => {
    const canvas = canvasRef.current; if (!canvas) return
    canvas.discardActiveObject()
    const objects = canvas.getObjects().filter(obj => !obj._isGrid && !obj._isSnapGuide && obj.selectable !== false)
    if (objects.length === 0) return
    const f = getFabric()
    canvas.setActiveObject(new f.ActiveSelection(objects, { canvas })); canvas.requestRenderAll()
  }, [])

  const deleteSelected = useCallback(() => {
    const canvas = canvasRef.current; if (!canvas) return
    const active = canvas.getActiveObject(); if (!active) return
    if (active.type === 'activeSelection') { active.forEachObject((obj) => canvas.remove(obj)) } else { canvas.remove(active) }
    canvas.discardActiveObject(); canvas.requestRenderAll()
  }, [])

  // ==========================================================================
  // OBJECT HELPERS
  // ==========================================================================
  const assignLayerId = (obj, name) => {
    layerCountRef.current++
    obj._layerId = `layer_${Date.now()}_${layerCountRef.current}`
    obj._layerName = name || `${(obj.type || 'Object').charAt(0).toUpperCase() + (obj.type || 'Object').slice(1)} ${layerCountRef.current}`
  }

  const addToCanvas = useCallback((obj, name) => {
    const canvas = canvasRef.current; if (!canvas) return
    assignLayerId(obj, name)
    if (obj.type !== 'image' && obj.type !== 'line') {
      obj.set({ fill: fillColor, stroke: strokeColor !== 'transparent' ? strokeColor : undefined, strokeWidth })
    }
    canvas.add(obj); canvas.centerObject(obj); canvas.setActiveObject(obj); canvas.requestRenderAll()
    setActiveTool('select'); setShowShapeMenu(false)
  }, [fillColor, strokeColor, strokeWidth])

  // ==========================================================================
  // SHAPES (basic)
  // ==========================================================================
  const addShape = useCallback((shapeType) => {
    const f = getFabric(); if (!f || !canvasRef.current) return
    let obj; const s = 120
    switch (shapeType) {
      case 'rect': obj = new f.Rect({ width: s * 1.4, height: s, rx: 4, ry: 4 }); break
      case 'circle': obj = new f.Circle({ radius: s / 2 }); break
      case 'triangle': obj = new f.Triangle({ width: s, height: s }); break
      case 'star': obj = new f.Polygon(createStarPoints(0, 0, s / 2, s / 4.5, 5)); break
      case 'heart': obj = new f.Path(createHeartPath(s)); break
      case 'diamond': obj = new f.Polygon([{ x: 0, y: -s / 2 }, { x: s / 2.5, y: 0 }, { x: 0, y: s / 2 }, { x: -s / 2.5, y: 0 }]); break
      case 'hexagon': obj = new f.Polygon(createHexagonPoints(0, 0, s / 2)); break
      case 'arrow': obj = new f.Polygon(createArrowPoints()); obj.set({ scaleX: 1.2, scaleY: 0.8 }); break
      case 'line': obj = new f.Line([0, 0, s * 2, 0], { stroke: fillColor, strokeWidth: 3, fill: undefined }); break
      default: return
    }
    addToCanvas(obj, `${SHAPES[shapeType]?.label || shapeType} ${layerCountRef.current + 1}`)
  }, [addToCanvas, fillColor])

  // ==========================================================================
  // SHAPE LIBRARY (V1.5)
  // ==========================================================================
  const addLibraryShape = useCallback((shape) => {
    const f = getFabric(); if (!f || !canvasRef.current) return
    const obj = new f.Path(shape.path, { scaleX: shape.scale || 1, scaleY: shape.scale || 1, originX: 'center', originY: 'center' })
    addToCanvas(obj, shape.name)
  }, [addToCanvas])

  // ==========================================================================
  // TEXT
  // ==========================================================================
  const addText = useCallback((text = 'Double-click to edit', options = {}) => {
    const f = getFabric(); if (!f || !canvasRef.current) return
    const canvas = canvasRef.current
    const {
      fontSize = 32,
      fontFamily = 'Arial',
      fill = fillColor,
      fontWeight = 'normal',
      fontStyle = 'normal',
      textAlign = 'center',
      top: posTop,
      left: posLeft,
    } = options
    const textObj = new f.IText(text, {
      fontSize, fontFamily, fill, fontWeight, fontStyle, textAlign, editable: true,
      left: posLeft ?? canvas.width / 2,
      top: posTop ?? canvas.height * 0.75,
      originX: 'center', originY: 'center',
    })
    addToCanvas(textObj, `Text ${layerCountRef.current + 1}`)
  }, [addToCanvas, fillColor])

  // ==========================================================================
  // IMAGE
  // ==========================================================================
  const addImage = useCallback((url) => {
    const f = getFabric(); if (!f || !canvasRef.current) return
    return new Promise((resolve, reject) => {
      f.Image.fromURL(url, (img) => {
        if (!img) { reject(new Error('Failed')); return }
        const canvas = canvasRef.current
        const scale = Math.min(canvas.width * 0.8 / img.width, canvas.height * 0.8 / img.height, 1)
        img.set({ scaleX: scale, scaleY: scale })
        addToCanvas(img, `Image ${layerCountRef.current + 1}`)
        resolve(img)
      }, { crossOrigin: 'anonymous' })
    })
  }, [addToCanvas])

  const setMainImage = useCallback((url) => {
    const f = getFabric(); if (!f || !canvasRef.current) return
    return new Promise((resolve, reject) => {
      // Pre-load image to get reliable dimensions before Fabric.js processes it
      const htmlImg = new Image()
      htmlImg.crossOrigin = 'anonymous'
      htmlImg.onload = () => {
        const naturalW = htmlImg.naturalWidth || htmlImg.width
        const naturalH = htmlImg.naturalHeight || htmlImg.height
        console.log(`setMainImage: natural ${naturalW}x${naturalH}`)
        
        f.Image.fromURL(url, (img) => {
          if (!img) { reject(new Error('Failed')); return }
          const canvas = canvasRef.current
          canvas.clear(); canvas.backgroundColor = '#ffffff'
          
          // Use reliable natural dimensions for scale calculation
          const imgW = naturalW || img.width || 100
          const imgH = naturalH || img.height || 100
          const scale = Math.min(canvas.width * 0.9 / imgW, canvas.height * 0.9 / imgH, 1)
          console.log(`setMainImage: canvas ${canvas.width}x${canvas.height}, scale ${scale.toFixed(4)}`)
          
          img.set({ scaleX: scale, scaleY: scale })
          assignLayerId(img, 'Main Image')
          canvas.add(img); canvas.centerObject(img); canvas.renderAll()
          pushHistory(); syncLayers()
          resolve(img)
        }, { crossOrigin: 'anonymous' })
      }
      htmlImg.onerror = () => {
        // Fallback: try Fabric directly (works for most URLs)
        f.Image.fromURL(url, (img) => {
          if (!img) { reject(new Error('Failed')); return }
          const canvas = canvasRef.current
          canvas.clear(); canvas.backgroundColor = '#ffffff'
          const imgW = img.width || 100
          const imgH = img.height || 100
          const scale = Math.min(canvas.width * 0.9 / imgW, canvas.height * 0.9 / imgH, 1)
          img.set({ scaleX: scale, scaleY: scale })
          assignLayerId(img, 'Main Image')
          canvas.add(img); canvas.centerObject(img); canvas.renderAll()
          pushHistory(); syncLayers()
          resolve(img)
        }, { crossOrigin: 'anonymous' })
      }
      htmlImg.src = url
    })
  }, [pushHistory, syncLayers])

  const handleFileUpload = useCallback((e) => {
    const file = e.target.files?.[0]; if (!file) return
    const reader = new FileReader()
    reader.onload = () => addImage(reader.result)
    reader.readAsDataURL(file); e.target.value = ''
  }, [addImage])

  // ==========================================================================
  // GRID
  // ==========================================================================
  const toggleGrid = useCallback(() => {
    const f = getFabric(); const canvas = canvasRef.current; if (!f || !canvas) return
    canvas.getObjects().filter(obj => obj._isGrid && !obj._isSnapGuide).forEach(obj => canvas.remove(obj))
    if (!showGrid) {
      const w = canvas.width, h = canvas.height
      for (let x = 0; x <= w; x += GRID_SIZE) { const l = new f.Line([x, 0, x, h], { stroke: '#e2e8f0', strokeWidth: 0.5, selectable: false, evented: false, _isGrid: true }); canvas.add(l); canvas.sendToBack(l) }
      for (let y = 0; y <= h; y += GRID_SIZE) { const l = new f.Line([0, y, w, y], { stroke: '#e2e8f0', strokeWidth: 0.5, selectable: false, evented: false, _isGrid: true }); canvas.add(l); canvas.sendToBack(l) }
    }
    setShowGrid(!showGrid); canvas.renderAll()
  }, [showGrid])

  // ==========================================================================
  // ZOOM
  // ==========================================================================
  const zoomTo = useCallback((level) => { const c = canvasRef.current; if (!c) return; const ct = c.getCenter(); c.zoomToPoint({ x: ct.left, y: ct.top }, level); setZoom(level) }, [])
  const zoomIn = useCallback(() => zoomTo(Math.min(zoom * 1.2, MAX_ZOOM)), [zoom, zoomTo])
  const zoomOut = useCallback(() => zoomTo(Math.max(zoom / 1.2, MIN_ZOOM)), [zoom, zoomTo])
  const zoomReset = useCallback(() => zoomTo(1), [zoomTo])

  // ==========================================================================
  // EXPORT
  // ==========================================================================
  const exportCanvas = useCallback((fmt = 'png') => {
    const c = canvasRef.current; if (!c) return null
    if (fmt === 'svg') return c.toSVG()
    if (fmt === 'json') return c.toJSON(['_layerId', '_layerName', '_layerLocked'])
    return c.toDataURL({ format: fmt === 'jpg' ? 'jpeg' : fmt, quality: 1, multiplier: 2 })
  }, [])

  const getCanvasDataUrl = useCallback(() => canvasRef.current?.toDataURL({ format: 'png', quality: 1, multiplier: 2 }) || null, [])

  // ==========================================================================
  // TEMPLATE
  // ==========================================================================
  const loadTemplate = useCallback((template) => {
    const f = getFabric(); const canvas = canvasRef.current; if (!f || !canvas) return
    canvas.clear(); canvas.backgroundColor = template.backgroundColor || '#ffffff'
    if (template.width && template.height) setCanvasSize({ width: template.width, height: template.height })
    if (template.json) {
      isUndoRedoRef.current = true
      canvas.loadFromJSON(template.json, () => { canvas.renderAll(); syncLayers(); isUndoRedoRef.current = false; pushHistory() })
    } else {
      if (template.guides) {
        template.guides.forEach((g) => {
          canvas.add(new f.Rect({ left: g.x, top: g.y, width: g.width, height: g.height, fill: 'transparent', stroke: g.color || '#06b6d4', strokeWidth: 1, strokeDashArray: [5, 5], selectable: false, evented: false, _isGrid: true }))
        })
        if (template.name) {
          canvas.add(new f.IText(template.name, { left: 10, top: 10, fontSize: 14, fill: '#94a3b8', fontFamily: 'Arial', selectable: false, evented: false, _isGrid: true }))
        }
      }
      canvas.renderAll(); pushHistory(); syncLayers()
    }
  }, [pushHistory, syncLayers])

  const loadFromJSON = useCallback((json) => {
    const canvas = canvasRef.current; if (!canvas) return
    isUndoRedoRef.current = true
    canvas.loadFromJSON(json, () => { canvas.renderAll(); syncLayers(); isUndoRedoRef.current = false; pushHistory() })
  }, [pushHistory, syncLayers])

  // ==========================================================================
  // IMPERATIVE HANDLE
  // ==========================================================================
  useImperativeHandle(ref, () => ({
    getCanvas: () => canvasRef.current,
    isReady: () => isReady,
    addImage, setMainImage, addShape, addText, undo, redo,
    exportCanvas, getCanvasDataUrl,
    toJSON: () => canvasRef.current?.toJSON(['_layerId', '_layerName', '_layerLocked']),
    toSVG: () => canvasRef.current?.toSVG(),
    loadTemplate, loadFromJSON,
    clear: () => { canvasRef.current?.clear(); if (canvasRef.current) canvasRef.current.backgroundColor = '#ffffff'; canvasRef.current?.renderAll(); syncLayers(); pushHistory() },
    deleteSelected, selectAll, groupSelected, ungroupSelected,
    booleanOp, mirrorDesign, tilePattern,
    // V1.3 Advanced Vector
    enterNodeEditMode, exitNodeEditMode, handleStrokeToPath, handlePathOffset, handleTextOnPath,
    // V2.0 Layout & Preview
    autoArrange, nestObjects, multiHoopSplit, applyTexture,
    getActiveObject: () => canvasRef.current?.getActiveObject(),
    discardSelection: () => { canvasRef.current?.discardActiveObject(); canvasRef.current?.renderAll() },
  }), [isReady, addImage, setMainImage, addShape, addText, undo, redo, exportCanvas, getCanvasDataUrl, loadTemplate, loadFromJSON, deleteSelected, selectAll, groupSelected, ungroupSelected, booleanOp, mirrorDesign, tilePattern, enterNodeEditMode, exitNodeEditMode, handleStrokeToPath, handlePathOffset, handleTextOnPath, autoArrange, nestObjects, multiHoopSplit, applyTexture, pushHistory, syncLayers])

  // ==========================================================================
  // RENDER
  // ==========================================================================
  const canUndo = historyIndexRef.current > 0
  const canRedo = historyIndexRef.current < historyRef.current.length - 1

  const COLOR_PALETTE = ['#8B5CF6','#06B6D4','#10B981','#F59E0B','#EF4444','#EC4899','#3B82F6','#6D28D9','#0891B2','#059669','#D97706','#DC2626','#DB2777','#2563EB','#1E1B4B','#164E63','#064E3B','#78350F','#7F1D1D','#831843','#1E3A5F','#FFFFFF','#F8FAFC','#E2E8F0','#94A3B8','#475569','#1E293B','#0F172A']

  const ToolBtn = ({ active, onClick, title, children, className: cls = '' }) => (
    <button onClick={onClick} className={`p-2 rounded-lg transition-colors ${active ? 'bg-zenna-violet/10 text-zenna-violet' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'} ${cls}`} title={title}>{children}</button>
  )

  return (
    <div ref={containerRef} className={`flex flex-1 overflow-hidden ${className}`}>
      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* TOOLBAR ROW 1 — Main tools */}
        <div className="flex-shrink-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-2 py-1 flex items-center gap-1 overflow-x-auto" style={{scrollbarWidth:'none',msOverflowStyle:'none', position:'relative', zIndex: 9998}}>
          {/* Select */}
          <ToolBtn active={activeTool === 'select'} onClick={() => { setActiveTool('select'); if (penPoints.length > 0) finalizePenPath() }} title="Select (V)">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" /></svg>
          </ToolBtn>

          {/* Pen Tool (V1.3) */}
          <ToolBtn active={activeTool === 'pen'} onClick={() => { if (activeTool === 'pen') { finalizePenPath() } else { setActiveTool('pen'); setPenPoints([]) } }} title="Pen Tool (P)">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
          </ToolBtn>
          <div className="w-px h-6 bg-slate-200 dark:bg-slate-700 mx-0.5" />

          {/* Basic Shapes dropdown */}
          <div className="relative">
            <button onClick={() => { setShowShapeMenu(!showShapeMenu); setShowShapeLibrary(false) }} className={`p-2 rounded-lg transition-colors flex items-center gap-1 ${showShapeMenu ? 'bg-zenna-violet/10 text-zenna-violet' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'}`} title="Basic Shapes">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm0 8a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zm12 0a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" /></svg>
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
            </button>
            {showShapeMenu && (
              <div className="absolute top-full left-0 mt-1 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 py-1 z-50 w-44">
                {Object.entries(SHAPES).map(([key, shape]) => (
                  <button key={key} onClick={() => { addShape(key); setShowShapeMenu(false) }} className="w-full px-3 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-3">
                    <span className="text-base w-5 text-center">{shape.icon}</span><span>{shape.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Shape Library (V1.5) */}
          <div className="relative">
            <ToolBtn active={showShapeLibrary} onClick={() => { setShowShapeLibrary(!showShapeLibrary); setShowShapeMenu(false) }} title="Shape Library">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
            </ToolBtn>
            <ShapeLibraryPanel isOpen={showShapeLibrary} onClose={() => setShowShapeLibrary(false)} onAddShape={addLibraryShape} />
          </div>

          {/* Text */}
          <button onClick={() => addText()} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors" title="Add Text">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="M4 7V4h16v3M9 20h6M12 4v16" /></svg>
          </button>

          {/* Image */}
          <button onClick={() => fileInputRef.current?.click()} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors" title="Add Image">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
          </button>
          <div className="w-px h-6 bg-slate-200 dark:bg-slate-700 mx-0.5" />

          {/* Boolean Ops (V1.3) */}
          <div className="flex items-center gap-0.5">
            <button onClick={() => booleanOp('union')} className="p-1.5 rounded text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 text-[10px] font-medium" title="Union (merge shapes)">∪</button>
            <button onClick={() => booleanOp('subtract')} className="p-1.5 rounded text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 text-[10px] font-medium" title="Subtract">−</button>
            <button onClick={() => booleanOp('intersect')} className="p-1.5 rounded text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 text-[10px] font-medium" title="Intersect">∩</button>
          </div>
          <div className="w-px h-6 bg-slate-200 dark:bg-slate-700 mx-0.5" />

          {/* Node Edit (V1.3) */}
          <ToolBtn active={activeTool === 'node-edit'} onClick={() => {
            if (activeTool === 'node-edit') exitNodeEditMode()
            else enterNodeEditMode()
          }} title="Node Editor — Edit path anchor points">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><circle cx="5" cy="5" r="2" /><circle cx="19" cy="5" r="2" /><circle cx="19" cy="19" r="2" /><circle cx="5" cy="19" r="2" /><path strokeLinecap="round" strokeWidth={1.5} d="M7 5h10M19 7v10M17 19H7M5 17V7" /></svg>
          </ToolBtn>

          {/* Inpaint / Edit Region */}
          <ToolBtn
            active={maskMode}
            onClick={() => {
              if (maskMode) {
                setMaskMode(false)
                setMaskPrompt('')
              } else {
                setMaskMode(true)
                setActiveTool('select')
              }
            }}
            title="Edit Region — Paint over an area to regenerate it"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              <circle cx="18" cy="18" r="4" fill="currentColor" opacity="0.3" />
            </svg>
          </ToolBtn>

          {/* Path Operations (V1.3) */}
          <div className="relative">
            <ToolBtn active={showPathOps} onClick={() => setShowPathOps(!showPathOps)} title="Path Operations">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" /></svg>
            </ToolBtn>
            {showPathOps && (
              <div className="absolute top-full left-0 mt-1 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 z-50 w-64 p-3 space-y-3">
                <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">Path Operations</div>

                {/* Stroke to Path */}
                <button onClick={() => { handleStrokeToPath(); setShowPathOps(false) }}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
                  <div className="text-xs font-medium text-slate-700 dark:text-slate-200">Stroke → Path</div>
                  <div className="text-[10px] text-slate-400">Convert outline to filled shape</div>
                </button>

                {/* Path Offset/Inset */}
                <div className="space-y-1.5">
                  <div className="text-[10px] text-slate-500 font-medium">Offset / Inset</div>
                  <div className="flex items-center gap-2">
                    <input type="range" min="2" max="30" value={offsetAmount}
                      onChange={(e) => setOffsetAmount(parseInt(e.target.value))}
                      className="flex-1 h-1" />
                    <span className="text-[10px] text-slate-400 w-8">{offsetAmount}px</span>
                  </div>
                  <div className="flex gap-1.5">
                    <button onClick={() => { handlePathOffset(offsetAmount); setShowPathOps(false) }}
                      className="flex-1 text-[10px] px-2 py-1.5 rounded-lg bg-zenna-violet/10 text-zenna-violet hover:bg-zenna-violet/20 font-medium">
                      + Offset
                    </button>
                    <button onClick={() => { handlePathOffset(-offsetAmount); setShowPathOps(false) }}
                      className="flex-1 text-[10px] px-2 py-1.5 rounded-lg bg-zenna-cyan/10 text-zenna-cyan hover:bg-zenna-cyan/20 font-medium">
                      − Inset
                    </button>
                  </div>
                </div>

                {/* Text on Path */}
                <div className="space-y-1.5 border-t border-slate-200 dark:border-slate-700 pt-2">
                  <div className="text-[10px] text-slate-500 font-medium">Text on Path <span className="text-slate-400">(select a path first)</span></div>
                  <input type="text" value={textOnPathInput} onChange={(e) => setTextOnPathInput(e.target.value)}
                    placeholder="Type text to place on path..."
                    className="w-full text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 focus:outline-none focus:ring-1 focus:ring-zenna-violet/50" />
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-slate-400">Size:</span>
                    <input type="range" min="12" max="72" value={textOnPathFontSize}
                      onChange={(e) => setTextOnPathFontSize(parseInt(e.target.value))} className="flex-1 h-1" />
                    <span className="text-[10px] text-slate-400 w-6">{textOnPathFontSize}</span>
                  </div>
                  <button onClick={() => { handleTextOnPath(); setShowPathOps(false) }}
                    disabled={!textOnPathInput.trim()}
                    className="w-full text-[10px] px-2 py-1.5 rounded-lg bg-zenna-violet text-white hover:bg-zenna-violet/80 font-medium disabled:opacity-30 disabled:cursor-not-allowed">
                    Place Text on Path
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="w-px h-6 bg-slate-200 dark:bg-slate-700 mx-0.5" />

          {/* Fill Color */}
          <div className="relative">
            <button onClick={() => setShowColorPicker(showColorPicker === 'fill' ? null : 'fill')} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-1" title="Fill Color">
              <div className="w-5 h-5 rounded border-2 border-slate-300 dark:border-slate-600" style={{ backgroundColor: fillColor }} />
              <span className="text-[10px] text-slate-400">Fill</span>
            </button>
            {showColorPicker === 'fill' && (
              <div className="absolute top-full left-0 mt-1 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 p-3 z-50">
                <div className="grid grid-cols-7 gap-1.5 mb-2">
                  {COLOR_PALETTE.map(c => (
                    <button key={c} onClick={() => { setFillColor(c); setShowColorPicker(null) }}
                      className={`w-6 h-6 rounded-md border-2 hover:scale-110 transition-transform ${fillColor === c ? 'border-zenna-violet scale-110' : 'border-slate-200 dark:border-slate-600'}`} style={{ backgroundColor: c }} />
                  ))}
                </div>
                <input type="color" value={fillColor} onChange={(e) => setFillColor(e.target.value)} className="w-full h-8 rounded cursor-pointer" />
              </div>
            )}
          </div>

          {/* Stroke Color */}
          <div className="relative">
            <button onClick={() => setShowColorPicker(showColorPicker === 'stroke' ? null : 'stroke')} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-1" title="Stroke">
              <div className="w-5 h-5 rounded border-2 border-slate-400 dark:border-slate-500 relative" style={{ borderColor: strokeColor === 'transparent' ? undefined : strokeColor }}>
                {strokeColor === 'transparent' && <div className="absolute inset-0 flex items-center justify-center"><div className="w-full h-[1.5px] bg-red-400 rotate-45" /></div>}
              </div>
              <span className="text-[10px] text-slate-400">Stroke</span>
            </button>
            {showColorPicker === 'stroke' && (
              <div className="absolute top-full left-0 mt-1 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 p-3 z-50">
                <button onClick={() => { setStrokeColor('transparent'); setStrokeWidth(0); setShowColorPicker(null) }} className="w-full mb-2 text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 flex items-center gap-1.5 py-1">
                  <div className="w-4 h-4 rounded border border-slate-300 relative"><div className="absolute inset-0 flex items-center justify-center"><div className="w-full h-[1px] bg-red-400 rotate-45" /></div></div>No stroke
                </button>
                <div className="grid grid-cols-7 gap-1.5 mb-2">
                  {['#8B5CF6','#06B6D4','#10B981','#F59E0B','#EF4444','#EC4899','#3B82F6','#0F172A','#1E293B','#475569','#94A3B8','#E2E8F0','#F8FAFC','#FFFFFF'].map(c => (
                    <button key={c} onClick={() => { setStrokeColor(c); setStrokeWidth(strokeWidth || 2); setShowColorPicker(null) }}
                      className={`w-6 h-6 rounded-md border-2 hover:scale-110 transition-transform ${strokeColor === c ? 'border-zenna-violet scale-110' : 'border-slate-200 dark:border-slate-600'}`} style={{ backgroundColor: c }} />
                  ))}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-[10px] text-slate-400">Width:</span>
                  <input type="range" min="1" max="10" value={strokeWidth || 2} onChange={(e) => setStrokeWidth(parseInt(e.target.value))} className="flex-1" />
                  <span className="text-xs text-slate-500 w-4">{strokeWidth || 2}</span>
                </div>
              </div>
            )}
          </div>
          <div className="w-px h-6 bg-slate-200 dark:bg-slate-700 mx-0.5" />

          {/* Undo/Redo */}
          <button onClick={undo} disabled={!canUndo} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-30" title="Undo (Ctrl+Z)">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" /></svg>
          </button>
          <button onClick={redo} disabled={!canRedo} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-30" title="Redo (Ctrl+Y)">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10H11a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" /></svg>
          </button>
          <div className="w-px h-6 bg-slate-200 dark:bg-slate-700 mx-0.5" />

          {/* Delete */}
          <button onClick={deleteSelected} className="p-2 rounded-lg text-slate-500 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20" title="Delete">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
          </button>

          <div className="flex-1" />

          {/* Alignment Tools toggle (V2.0) */}
          <ToolBtn active={showAlignTools} onClick={() => setShowAlignTools(!showAlignTools)} title="Align & Layout">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
          </ToolBtn>

          {/* Mirror (V2.0) */}
          <div className="relative group">
            <button className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700" title="Mirror/Tile">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>
            </button>
            <div className="absolute right-0 top-full mt-1 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 py-1 z-50 w-44 hidden group-hover:block">
              <button onClick={() => mirrorDesign('horizontal')} className="w-full px-3 py-2 text-left text-xs text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700">Mirror Horizontal</button>
              <button onClick={() => mirrorDesign('vertical')} className="w-full px-3 py-2 text-left text-xs text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700">Mirror Vertical</button>
              <div className="border-t border-slate-200 dark:border-slate-700 my-1" />
              <button onClick={() => tilePattern(3, 3)} className="w-full px-3 py-2 text-left text-xs text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700">Tile 3×3</button>
              <button onClick={() => tilePattern(4, 4)} className="w-full px-3 py-2 text-left text-xs text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700">Tile 4×4</button>
              <button onClick={() => tilePattern(2, 2)} className="w-full px-3 py-2 text-left text-xs text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700">Tile 2×2</button>
            </div>
          </div>

          {/* Layout (V2.0) */}
          <div className="relative group">
            <button className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700" title="Auto Layout & Nesting">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v5a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 011 1v5a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" /></svg>
            </button>
            <div className="absolute right-0 top-full mt-1 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 py-1 z-50 w-48 hidden group-hover:block">
              <div className="px-3 py-1 text-[10px] text-slate-400 font-medium uppercase">Auto Arrange</div>
              <button onClick={() => autoArrange('grid')} className="w-full px-3 py-2 text-left text-xs text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700">Grid Layout</button>
              <button onClick={() => autoArrange('horizontal')} className="w-full px-3 py-2 text-left text-xs text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700">Horizontal Row</button>
              <button onClick={() => autoArrange('circle')} className="w-full px-3 py-2 text-left text-xs text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700">Circle Layout</button>
              <div className="border-t border-slate-200 dark:border-slate-700 my-1" />
              <div className="px-3 py-1 text-[10px] text-slate-400 font-medium uppercase">Cutting</div>
              <button onClick={() => nestObjects()} className="w-full px-3 py-2 text-left text-xs text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700">Nest (minimize waste)</button>
              <div className="border-t border-slate-200 dark:border-slate-700 my-1" />
              <div className="px-3 py-1 text-[10px] text-slate-400 font-medium uppercase">Embroidery</div>
              <button onClick={() => multiHoopSplit(130, 180)} className="w-full px-3 py-2 text-left text-xs text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700">Multi-hoop 5×7</button>
              <button onClick={() => multiHoopSplit(200, 200)} className="w-full px-3 py-2 text-left text-xs text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700">Multi-hoop 8×8</button>
              <button onClick={() => multiHoopSplit(260, 160)} className="w-full px-3 py-2 text-left text-xs text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700">Multi-hoop 6×10</button>
            </div>
          </div>

          {/* Texture Preview (V2.0) */}
          <div className="relative">
            <ToolBtn active={showTexturePanel || !!activeTexture} onClick={() => setShowTexturePanel(!showTexturePanel)} title="Material Preview">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" /></svg>
            </ToolBtn>
            {showTexturePanel && (
              <div className="absolute right-0 top-full mt-1 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 z-50 w-56 max-h-72 overflow-y-auto">
                <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">Material Preview</span>
                  {activeTexture && (
                    <button onClick={() => { applyTexture(activeTexture); setShowTexturePanel(false) }} className="text-[10px] text-red-500 hover:text-red-600">Clear</button>
                  )}
                </div>
                {Object.entries(TEXTURES).map(([key, cat]) => (
                  <div key={key}>
                    <div className="px-3 py-1 text-[10px] text-slate-400 font-medium uppercase bg-slate-50 dark:bg-slate-700/50">{cat.label}</div>
                    {cat.items.map(tex => (
                      <button key={tex.id} onClick={() => { applyTexture(tex); setShowTexturePanel(false) }}
                        className={`w-full px-3 py-1.5 text-left text-xs flex items-center gap-2 transition-colors ${activeTexture?.id === tex.id ? 'bg-zenna-violet/10 text-zenna-violet' : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700'}`}>
                        <div className="w-5 h-5 rounded border border-slate-200 dark:border-slate-600" style={{ background: tex.bg || '#fff', backgroundImage: tex.pattern !== 'none' ? tex.pattern : undefined, backgroundSize: '6px 6px' }} />
                        {tex.name}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Grid */}
          <ToolBtn active={showGrid} onClick={toggleGrid} title="Grid">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5z M4 9h16 M4 13h16 M4 17h16 M8 4v16 M12 4v16 M16 4v16" /></svg>
          </ToolBtn>

          {/* Snap */}
          <button onClick={() => setSnapEnabled(!snapEnabled)} className={`p-2 rounded-lg ${snapEnabled ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'}`} title="Smart Snap">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
          </button>

          {/* Zoom */}
          <div className="flex items-center gap-0.5 bg-slate-50 dark:bg-slate-700 rounded-lg px-1 py-0.5">
            <button onClick={zoomOut} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-500">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" /></svg>
            </button>
            <button onClick={zoomReset} className="px-1.5 py-0.5 text-[10px] font-mono text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 min-w-[36px] text-center">{Math.round(zoom * 100)}%</button>
            <button onClick={zoomIn} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-500">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            </button>
          </div>

          {/* Layers toggle */}
          <ToolBtn active={showLayers} onClick={() => setShowLayers(!showLayers)} title="Layers">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
          </ToolBtn>
        </div>

        {/* TOOLBAR ROW 2 — Alignment (shown when toggled) */}
        {showAlignTools && (
          <div className="flex-shrink-0 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700 px-3 py-1 flex items-center gap-2">
            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mr-1">Align</span>
            <AlignmentToolbar canvas={canvasRef.current} pushHistory={pushHistory} />
          </div>
        )}

        {/* Pen tool status bar */}
        {activeTool === 'pen' && (
          <div className="flex-shrink-0 bg-zenna-violet/5 border-b border-zenna-violet/20 px-3 py-1.5 flex items-center gap-2">
            <span className="text-xs text-zenna-violet font-medium">✏️ Pen Tool</span>
            <span className="text-[10px] text-slate-500">{penPoints.length} points — Click to add points, Escape to finish</span>
            {penPoints.length >= 2 && (
              <button onClick={finalizePenPath} className="ml-auto text-[10px] bg-zenna-violet text-white px-2 py-0.5 rounded-full hover:bg-zenna-violet/80">Finish Path</button>
            )}
          </div>
        )}

        {/* Inpainting mask controls */}
        {maskMode && (
          <div className="flex-shrink-0 bg-red-50 dark:bg-red-900/10 border-b border-red-200 dark:border-red-800/30 px-3 py-2 flex items-center gap-3">
            <span className="text-xs text-red-600 dark:text-red-400 font-semibold shrink-0">🎨 Edit Region</span>
            <span className="text-[10px] text-slate-500 shrink-0">Paint over the area to change</span>

            {/* Brush size */}
            <div className="flex items-center gap-1.5 shrink-0">
              <span className="text-[10px] text-slate-400">Size:</span>
              <input
                type="range"
                min="10"
                max="100"
                value={maskBrushSize}
                onChange={(e) => setMaskBrushSize(parseInt(e.target.value))}
                className="w-16 h-1 accent-red-500"
              />
              <span className="text-[10px] text-slate-500 w-6">{maskBrushSize}</span>
            </div>

            {/* What to put in the masked area */}
            <input
              type="text"
              value={maskPrompt}
              onChange={(e) => setMaskPrompt(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleInpaintSubmit() }}
              placeholder="What should go here? (e.g. 'realistic crossed revolvers')"
              className="flex-1 px-3 py-1.5 text-xs rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 text-slate-800 dark:text-white placeholder-slate-400 focus:outline-none focus:border-red-400 min-w-[200px]"
            />

            <button
              onClick={handleInpaintSubmit}
              disabled={!maskPrompt.trim() || maskSubmitting}
              className="px-3 py-1.5 text-xs font-medium bg-red-500 text-white rounded-lg hover:bg-red-600 transition disabled:opacity-40 shrink-0"
            >
              {maskSubmitting ? 'Generating...' : 'Apply'}
            </button>

            {/* Clear mask */}
            <button
              onClick={() => {
                const mc = maskCanvasRef.current
                if (mc) mc.getContext('2d').clearRect(0, 0, mc.width, mc.height)
              }}
              className="px-2 py-1.5 text-[10px] text-slate-500 hover:text-red-500 transition shrink-0"
            >
              Clear
            </button>

            <button
              onClick={() => { setMaskMode(false); setMaskPrompt('') }}
              className="px-2 py-1.5 text-[10px] text-slate-400 hover:text-white transition shrink-0"
            >
              Cancel
            </button>
          </div>
        )}

        {/* Node edit status bar (V1.3) */}
        {activeTool === 'node-edit' && (
          <div className="flex-shrink-0 bg-cyan-50 dark:bg-cyan-900/10 border-b border-cyan-200 dark:border-cyan-800/30 px-3 py-1.5 flex items-center gap-2">
            <span className="text-xs text-cyan-700 dark:text-cyan-400 font-medium">◇ Node Editor</span>
            <span className="text-[10px] text-slate-500">{nodeAnchors.filter(a => a.type === 'anchor').length} anchors, {nodeAnchors.filter(a => a.type === 'control').length} handles — Drag points to reshape. Escape to exit.</span>
            <div className="ml-auto flex items-center gap-1.5">
              <button onClick={addNodeToPath} className="text-[10px] bg-cyan-600 text-white px-2 py-0.5 rounded-full hover:bg-cyan-700">+ Add Node</button>
              <button onClick={deleteNodeFromPath} className="text-[10px] bg-slate-500 text-white px-2 py-0.5 rounded-full hover:bg-slate-600">− Remove Node</button>
              <button onClick={exitNodeEditMode} className="text-[10px] bg-slate-400 text-white px-2 py-0.5 rounded-full hover:bg-slate-500">Done</button>
            </div>
          </div>
        )}

        {/* CANVAS */}
        {/* Text Properties — moved to Layers panel sidebar. Old floating bar removed. */}
        {false && (
          <div>

            {/* Font family — searchable with categories + previews in actual font */}
            <div className="relative">
              <input
                type="text"
                value={fontSearch || activeObject.fontFamily || 'Arial'}
                onChange={(e) => {
                  const q = e.target.value
                  setFontSearch(q)
                  setShowFontDropdown(true)
                  if (fontTimerRef.current) clearTimeout(fontTimerRef.current)
                  fontTimerRef.current = setTimeout(async () => {
                    try {
                      if (!fontCacheRef.current) {
                        const res = await fetch(`https://www.googleapis.com/webfonts/v1/webfonts?key=AIzaSyDZKSU6e1rl5ccUVh0aLv_a8XEMSmFROvY&sort=popularity`)
                        const data = await res.json()
                        fontCacheRef.current = (data.items || []).map(f => ({ family: f.family, category: f.category }))
                      }
                      let matches = fontCacheRef.current
                      if (q && q.length >= 1) {
                        matches = matches.filter(f => f.family.toLowerCase().includes(q.toLowerCase()))
                      }
                      const filtered = activeFontCategory
                        ? matches.filter(f => f.category === activeFontCategory)
                        : matches
                      setFontResults(filtered.slice(0, 30).map(f => f.family))
                      // Preload visible fonts for preview
                      filtered.slice(0, 30).forEach(f => {
                        const linkId = `gfont-${f.family.replace(/\s/g, '-')}`
                        if (!document.getElementById(linkId)) {
                          const link = document.createElement('link')
                          link.id = linkId
                          link.rel = 'stylesheet'
                          link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(f.family)}&display=swap`
                          document.head.appendChild(link)
                        }
                      })
                    } catch (e) { setFontResults([]) }
                  }, 200)
                }}
                onFocus={() => {
                  setShowFontDropdown(true)
                  // Load initial popular fonts if not cached
                  if (!fontCacheRef.current) {
                    const q = fontSearch || ''
                    const fakeEvent = { target: { value: q } }
                    // Trigger the onChange to load fonts
                    setFontSearch(q || '')
                    if (fontTimerRef.current) clearTimeout(fontTimerRef.current)
                    fontTimerRef.current = setTimeout(async () => {
                      try {
                        const res = await fetch(`https://www.googleapis.com/webfonts/v1/webfonts?key=AIzaSyDZKSU6e1rl5ccUVh0aLv_a8XEMSmFROvY&sort=popularity`)
                        const data = await res.json()
                        fontCacheRef.current = (data.items || []).map(f => ({ family: f.family, category: f.category }))
                        const initial = fontCacheRef.current.slice(0, 30)
                        setFontResults(initial.map(f => f.family))
                        initial.forEach(f => {
                          const linkId = `gfont-${f.family.replace(/\s/g, '-')}`
                          if (!document.getElementById(linkId)) {
                            const link = document.createElement('link')
                            link.id = linkId
                            link.rel = 'stylesheet'
                            link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(f.family)}&display=swap`
                            document.head.appendChild(link)
                          }
                        })
                      } catch (e) {}
                    }, 100)
                  } else {
                    const filtered = activeFontCategory
                      ? fontCacheRef.current.filter(f => f.category === activeFontCategory)
                      : fontCacheRef.current
                    const results = filtered.slice(0, 30)
                    setFontResults(results.map(f => f.family))
                    results.forEach(f => {
                      const linkId = `gfont-${f.family.replace(/\s/g, '-')}`
                      if (!document.getElementById(linkId)) {
                        const link = document.createElement('link')
                        link.id = linkId
                        link.rel = 'stylesheet'
                        link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(f.family)}&display=swap`
                        document.head.appendChild(link)
                      }
                    })
                  }
                }}
                onBlur={() => setTimeout(() => setShowFontDropdown(false), 300)}
                placeholder="Search 2,500+ fonts..."
                className="text-xs px-2 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-800 dark:text-white w-[180px]"
              />
              {showFontDropdown && (
                <div className="absolute top-full left-0 mt-1 w-80 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-2xl flex flex-col max-h-[400px]" style={{ zIndex: 9999 }}>
                  {/* Category filter tags */}
                  <div className="flex flex-wrap gap-1 p-2 border-b border-slate-200 dark:border-slate-700">
                    {[
                      { key: null, label: 'All' },
                      { key: 'serif', label: 'Serif' },
                      { key: 'sans-serif', label: 'Sans' },
                      { key: 'display', label: 'Display' },
                      { key: 'handwriting', label: 'Handwriting' },
                      { key: 'monospace', label: 'Mono' },
                    ].map(cat => (
                      <button
                        key={cat.key || 'all'}
                        onMouseDown={(e) => {
                          e.preventDefault()
                          setActiveFontCategory(cat.key)
                          const filtered = cat.key
                            ? (fontCacheRef.current || []).filter(f => f.category === cat.key)
                            : (fontCacheRef.current || [])
                          const q = fontSearch || ''
                          const searched = q ? filtered.filter(f => f.family.toLowerCase().includes(q.toLowerCase())) : filtered
                          const results = searched.slice(0, 30)
                          setFontResults(results.map(f => f.family))
                          results.forEach(f => {
                            const linkId = `gfont-${f.family.replace(/\s/g, '-')}`
                            if (!document.getElementById(linkId)) {
                              const link = document.createElement('link')
                              link.id = linkId
                              link.rel = 'stylesheet'
                              link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(f.family)}&display=swap`
                              document.head.appendChild(link)
                            }
                          })
                        }}
                        className={`px-2 py-0.5 text-[10px] font-medium rounded-full transition ${
                          activeFontCategory === cat.key
                            ? 'bg-zenna-violet text-white'
                            : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200'
                        }`}
                      >{cat.label}</button>
                    ))}
                  </div>
                  {/* Font list with previews */}
                  <div className="flex-1 overflow-y-auto">
                    {fontResults.map(f => (
                      <button
                        key={f}
                        onMouseDown={(e) => {
                          e.preventDefault()
                          const linkId = `gfont-${f.replace(/\s/g, '-')}`
                          if (!document.getElementById(linkId)) {
                            const link = document.createElement('link')
                            link.id = linkId
                            link.rel = 'stylesheet'
                            link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(f)}&display=swap`
                            document.head.appendChild(link)
                          }
                          setTimeout(() => {
                            activeObject.set('fontFamily', f)
                            canvasRef.current?.renderAll()
                            pushHistory()
                          }, 300)
                          setFontSearch(f)
                          setShowFontDropdown(false)
                        }}
                        className="w-full text-left px-3 py-2.5 hover:bg-zenna-violet/10 transition border-b border-slate-100 dark:border-slate-700/50 flex items-center justify-between"
                      >
                        <span className="text-base text-slate-800 dark:text-white truncate" style={{ fontFamily: `'${f}', sans-serif` }}>{f}</span>
                        <span className="text-[9px] text-slate-400 ml-2 shrink-0">Aa</span>
                      </button>
                    ))}
                    {fontResults.length === 0 && (
                      <div className="text-center text-xs text-slate-400 py-6">
                        {fontSearch ? 'No fonts match your search' : 'Loading fonts...'}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Font size */}
            <input
              type="number"
              value={Math.round(activeObject.fontSize || 32)}
              onChange={(e) => {
                activeObject.set('fontSize', parseInt(e.target.value) || 32)
                canvasRef.current?.renderAll()
                pushHistory()
              }}
              className="text-xs w-14 px-2 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-800 dark:text-white text-center"
              min="8"
              max="200"
            />

            {/* Bold */}
            <button
              onClick={() => {
                activeObject.set('fontWeight', activeObject.fontWeight === 'bold' ? 'normal' : 'bold')
                canvasRef.current?.renderAll()
                pushHistory()
              }}
              className={`p-1.5 rounded-lg text-xs font-bold transition ${activeObject.fontWeight === 'bold' ? 'bg-zenna-violet text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'}`}
              title="Bold"
            >
              B
            </button>

            {/* Italic */}
            <button
              onClick={() => {
                activeObject.set('fontStyle', activeObject.fontStyle === 'italic' ? 'normal' : 'italic')
                canvasRef.current?.renderAll()
                pushHistory()
              }}
              className={`p-1.5 rounded-lg text-xs italic transition ${activeObject.fontStyle === 'italic' ? 'bg-zenna-violet text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'}`}
              title="Italic"
            >
              I
            </button>

            {/* Text color */}
            <div className="relative">
              <input
                type="color"
                value={activeObject.fill || '#000000'}
                onChange={(e) => {
                  activeObject.set('fill', e.target.value)
                  canvasRef.current?.renderAll()
                  pushHistory()
                }}
                className="w-7 h-7 rounded cursor-pointer border border-slate-200 dark:border-slate-600"
                title="Text color"
              />
            </div>

            {/* Alignment */}
            {['left', 'center', 'right'].map(align => (
              <button
                key={align}
                onClick={() => {
                  activeObject.set('textAlign', align)
                  canvasRef.current?.renderAll()
                  pushHistory()
                }}
                className={`p-1.5 rounded-lg text-[10px] transition ${activeObject.textAlign === align ? 'bg-zenna-violet text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-500'}`}
                title={`Align ${align}`}
              >
                {align === 'left' ? '◧' : align === 'center' ? '◫' : '◨'}
              </button>
            ))}

            <div className="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-1" />

            {/* Text layout / curve options */}
            {[
              { id: 'straight', label: '—', title: 'Straight' },
              { id: 'curved-up', label: '⌒', title: 'Curve Up' },
              { id: 'curved-down', label: '⌓', title: 'Curve Down' },
              { id: 'circle', label: '○', title: 'Circle' },
            ].map(layout => (
              <button
                key={layout.id}
                onClick={() => {
                  const f = getFabric()
                  if (!f || !canvasRef.current) return
                  const canvas = canvasRef.current
                  const text = activeObject.text || activeObject.getText?.() || 'Text'
                  const opts = {
                    fontSize: activeObject.fontSize || 32,
                    fontFamily: activeObject.fontFamily || 'Arial',
                    fill: activeObject.fill || '#000000',
                    fontWeight: activeObject.fontWeight || 'normal',
                    fontStyle: activeObject.fontStyle || 'normal',
                  }
                  const centerX = activeObject.left || canvas.width / 2
                  const centerY = activeObject.top || canvas.height / 2

                  if (layout.id === 'straight') {
                    // Already straight IText — nothing to do unless it was curved
                    return
                  }

                  // Remove old text object
                  canvas.remove(activeObject)

                  // Create curved text using individual characters on a path
                  const chars = text.split('')
                  const radius = opts.fontSize * 3
                  const group = []

                  if (layout.id === 'curved-up' || layout.id === 'curved-down') {
                    const isUp = layout.id === 'curved-up'
                    const arcAngle = Math.PI * 0.6
                    const startAngle = isUp ? Math.PI + (Math.PI - arcAngle) / 2 : (Math.PI - arcAngle) / 2
                    const angleStep = chars.length > 1 ? arcAngle / (chars.length - 1) : 0

                    chars.forEach((ch, i) => {
                      const angle = startAngle + (i * angleStep)
                      const x = centerX + radius * Math.cos(angle)
                      const y = centerY + radius * Math.sin(angle) * (isUp ? 1 : -1)
                      const rotation = ((angle - Math.PI / 2) * 180 / Math.PI) * (isUp ? 1 : -1)
                      const charObj = new f.Text(ch, {
                        ...opts,
                        left: x, top: y,
                        angle: isUp ? rotation + 180 : rotation,
                        originX: 'center', originY: 'center',
                        selectable: false,
                      })
                      group.push(charObj)
                    })
                  } else if (layout.id === 'circle') {
                    const angleStep = (2 * Math.PI) / chars.length
                    chars.forEach((ch, i) => {
                      const angle = -Math.PI / 2 + i * angleStep
                      const x = centerX + radius * Math.cos(angle)
                      const y = centerY + radius * Math.sin(angle)
                      const charObj = new f.Text(ch, {
                        ...opts,
                        left: x, top: y,
                        angle: (angle + Math.PI / 2) * 180 / Math.PI,
                        originX: 'center', originY: 'center',
                        selectable: false,
                      })
                      group.push(charObj)
                    })
                  }

                  if (group.length > 0) {
                    const grp = new f.Group(group, { left: centerX - radius, top: centerY - radius })
                    canvas.add(grp)
                    canvas.setActiveObject(grp)
                    canvas.renderAll()
                    pushHistory()
                    syncLayers()
                  }
                }}
                className="p-1.5 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-500 hover:text-zenna-violet hover:bg-zenna-violet/10 text-sm transition"
                title={layout.title}
              >
                {layout.label}
              </button>
            ))}

            {/* Bring to front */}
            <button
              onClick={() => {
                canvasRef.current?.bringToFront(activeObject)
                canvasRef.current?.renderAll()
                syncLayers()
              }}
              className="p-1.5 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-500 hover:text-white text-[10px] transition ml-auto"
              title="Bring text to front"
            >
              ↑ Front
            </button>

            <span className="text-[10px] text-slate-500 hidden sm:inline">Double-click to edit</span>
          </div>
        )}

        <div className="flex-1 relative overflow-hidden bg-slate-100 dark:bg-slate-900" style={{ zIndex: 1 }}>
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(45deg, #f0f0f0 25%, transparent 25%), linear-gradient(-45deg, #f0f0f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f0f0f0 75%), linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)',
            backgroundSize: '20px 20px', backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px', opacity: 0.3,
          }} />
          <canvas ref={canvasElRef} />
          {/* Inpainting mask overlay — paint over areas to regenerate */}
          {maskMode && (
            <canvas
              ref={maskCanvasRef}
              className="absolute inset-0 z-20 cursor-crosshair"
              style={{ mixBlendMode: 'multiply', opacity: 0.5 }}
              onMouseDown={(e) => {
                const c = maskCanvasRef.current
                if (!c) return
                const ctx = c.getContext('2d')
                const rect = c.getBoundingClientRect()
                ctx.fillStyle = 'rgba(255, 0, 0, 0.6)'
                ctx.beginPath()
                ctx.arc(e.clientX - rect.left, e.clientY - rect.top, maskBrushSize / 2, 0, Math.PI * 2)
                ctx.fill()
                c._painting = true
              }}
              onMouseMove={(e) => {
                const c = maskCanvasRef.current
                if (!c || !c._painting) return
                const ctx = c.getContext('2d')
                const rect = c.getBoundingClientRect()
                ctx.fillStyle = 'rgba(255, 0, 0, 0.6)'
                ctx.beginPath()
                ctx.arc(e.clientX - rect.left, e.clientY - rect.top, maskBrushSize / 2, 0, Math.PI * 2)
                ctx.fill()
              }}
              onMouseUp={() => { if (maskCanvasRef.current) maskCanvasRef.current._painting = false }}
              onMouseLeave={() => { if (maskCanvasRef.current) maskCanvasRef.current._painting = false }}
              onTouchStart={(e) => {
                const c = maskCanvasRef.current
                if (!c) return
                e.preventDefault()
                const ctx = c.getContext('2d')
                const rect = c.getBoundingClientRect()
                const t = e.touches[0]
                ctx.fillStyle = 'rgba(255, 0, 0, 0.6)'
                ctx.beginPath()
                ctx.arc(t.clientX - rect.left, t.clientY - rect.top, maskBrushSize / 2, 0, Math.PI * 2)
                ctx.fill()
                c._painting = true
              }}
              onTouchMove={(e) => {
                const c = maskCanvasRef.current
                if (!c || !c._painting) return
                e.preventDefault()
                const ctx = c.getContext('2d')
                const rect = c.getBoundingClientRect()
                const t = e.touches[0]
                ctx.fillStyle = 'rgba(255, 0, 0, 0.6)'
                ctx.beginPath()
                ctx.arc(t.clientX - rect.left, t.clientY - rect.top, maskBrushSize / 2, 0, Math.PI * 2)
                ctx.fill()
              }}
              onTouchEnd={() => { if (maskCanvasRef.current) maskCanvasRef.current._painting = false }}
            />
          )}
          {/* Texture overlay (V2.0) */}
          {activeTexture && activeTexture.pattern !== 'none' && (
            <div className="absolute inset-0 pointer-events-none z-10" style={{
              backgroundImage: activeTexture.pattern,
              backgroundSize: '8px 8px',
              opacity: activeTexture.opacity || 0.3,
              mixBlendMode: 'multiply',
            }} />
          )}
          {isReady && layers.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center p-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-zenna-violet/20 to-zenna-cyan/20 flex items-center justify-center">
                  <svg className="w-8 h-8 text-zenna-violet" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-display font-semibold text-slate-700 dark:text-slate-200 mb-2">Your canvas awaits</h3>
                <p className="text-slate-500 dark:text-slate-400 text-sm max-w-xs mx-auto">Add shapes, text, or images from the toolbar. Use the Shape Library for decorative elements, or chat with Zenna to generate designs.</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* LAYERS PANEL — overlay on mobile, side panel on desktop */}
      {showLayers && (
        <>
          {/* Mobile backdrop — tap to close */}
          <div
            className="md:hidden fixed inset-0 bg-black/40 z-40"
            onClick={() => setShowLayers(false)}
          />
          {/* Panel — fixed overlay on mobile, normal side panel on desktop */}
          <div className="fixed md:relative right-0 top-0 md:top-auto h-full z-50 md:z-auto md:h-auto md:flex">
            <LayerPanel layers={layers} activeId={activeObjectId} activeObject={activeObject} canvas={canvasRef.current}
              onSelect={selectLayer} onToggleVisible={toggleLayerVisibility} onToggleLock={toggleLayerLock}
              onReorder={reorderLayer} onDelete={deleteLayer} onRename={renameLayer}
              onGroup={groupSelected} onUngroup={ungroupSelected} onEffectsUpdate={pushHistory} />
          </div>
        </>
      )}
    </div>
  )
})

CanvasEditor.displayName = 'CanvasEditor'
export default CanvasEditor